"use client"

import { useState } from "react"
import Image from "@/src/components/Image"
import { Button } from "@/components/ui/button"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Badge } from "@/components/ui/badge"
import { CheckCircle2, XCircle, Copy } from "lucide-react"
import { TabGroup, TabGroupList, TabGroupTrigger, TabGroupContent } from "@/components/ui/tab-group"
import { toast } from "sonner"
import { API_CONFIG } from "@/config/api.config"

// ============ UTILITY COMPONENTS ============
function EndpointHeader({
  title,
  method,
  endpoint,
  authRequired = true,
}: {
  title: string
  method: "GET" | "POST"
  endpoint: string
  authRequired?: boolean
}) {
  const methodColor = method === "GET" ? "bg-green-500" : "bg-blue-500"
  const fullUrl = `${API_CONFIG.BASE_URL}${endpoint}`
  
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(fullUrl)
    toast.success("คัดลอก URL สำเร็จ", {
      description: fullUrl,
      duration: 2000,
    })
  }
  
  return (
    <>
      <h3 className="text-2xl font-bold text-gray-900 mb-6">{title}</h3>
      <div className="mb-6 space-y-3">
        {/* Endpoint Method and Path */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-gray-700">Endpoint:</span>
            <Badge className={`${methodColor} hover:${methodColor} text-white px-3 py-1`}>
              {method}
            </Badge>
            <code className="text-blue-600 text-sm font-mono bg-blue-50 px-3 py-1 rounded">
              {endpoint}
            </code>
            {authRequired && (
              <Badge className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 ml-auto">
                Auth Required
              </Badge>
            )}
          </div>
        </div>
        
        {/* Full URL with Copy Button */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-xs font-semibold text-blue-700 mb-2">Full API URL:</p>
              <code className="text-sm font-mono text-blue-900 break-all block">
                {fullUrl}
              </code>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleCopyUrl}
              className="flex-shrink-0 h-8 w-8 p-0 hover:bg-blue-100"
              title="Copy URL"
            >
              <Copy className="w-4 h-4 text-blue-600" />
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

// Shared checkCondition request body data
const getCheckConditionFields = (_mainFieldKey: string) => [
  {
    key: "checkCondition",
    type: "Object",
    description: "Object ข้อมูลที่ต้องการตรวจสอบเงื่อนไขในเพิ่มเติม",
    example: "{ ... }",
    required: false,
    note: "*ระบุหากต้องการตรวจสอบเงื่อนไขในเพิ่มเติม",
  },
  {
    key: "checkCondition.checkDuplicate",
    type: "Boolean",
    description: "Flag สำหรับตรวจสอบสลิปซ้ำ",
    example: "true | false",
    required: false,
    note: "*ระบุหากต้องการตรวจสอบสลิปซ้ำ",
  },
  {
    key: "checkCondition.checkReceiver",
    type: "Array",
    description: "Array การตรวจสอบบัญชีผู้รับ",
    example: "[{ receiver1 }, { receiver2 }, ...]",
    required: false,
    note: "*ระบุได้แค่ 1 Object เท่านั้น เพื่อตรวจสอบบัญชีผู้รับ *สามารถกรองได้แค่ 1 วิธีเท่านั้น ถ้าส่งมาหลายวิธีจะถือเอาข้อมูลตัวแรก",
  },
  {
    key: "checkCondition.checkReceiver.accountType",
    type: "String",
    description: "ตรวจสอบประเภทบัญชีผู้รับ",
    example: (
      <div className="space-y-1">
        <div><span className="font-semibold">&quot;01002&quot;</span> = ธนาคารกรุงเทพ Bangkok Bank</div>
        <div><span className="font-semibold">&quot;01004&quot;</span> = ธนาคารกสิกรไทย (Kasikorn Bank)</div>
        <div><span className="font-semibold">&quot;01006&quot;</span> = ธนาคารกรุงไทย (Krung Thai Bank)</div>
        <div><span className="font-semibold">&quot;01011&quot;</span> = ธนาคารทหารไทยธนชาต (TMB Thanachart Bank)</div>
        <div><span className="font-semibold">&quot;01014&quot;</span> = ธนาคารไทยพาณิชย์ (SCB)</div>
        <div><span className="font-semibold">&quot;01025&quot;</span> = ธนาคารกรุงศรีอยุธยา (Krungsri Bank)</div>
        <div><span className="font-semibold">&quot;01069&quot;</span> = ธนาคารทหารไทยธนชาต (TMB Thanachart Bank)</div>
        <div><span className="font-semibold">&quot;01022&quot;</span> = ธนาคารซีไอเอ็มบีไทย (CIMB Thai Bank)</div>
        <div><span className="font-semibold">&quot;01067&quot;</span> = ธนาคารทิสโก้ (TISCO Bank)</div>
        <div><span className="font-semibold">&quot;01024&quot;</span> = ธนาคารยูโอบี (UOB)</div>
        <div><span className="font-semibold">&quot;01071&quot;</span> = ธนาคารไทยเครดิต (Thai Credit Bank)</div>
        <div><span className="font-semibold">&quot;01073&quot;</span> = ธนาคารแลนด์ แอนด์ เฮ้าส์ (LH Bank)</div>
        <div><span className="font-semibold">&quot;01070&quot;</span> = ธนาคารไอซีบีซี (ไทย) (ICBC Thai)</div>
        <div><span className="font-semibold">&quot;01098&quot;</span> = ธนาคารพัฒนาวิสาหกิจขนาดกลางและขนาดย่อมแห่งประเทศไทย (SME Bank)</div>
        <div><span className="font-semibold">&quot;01034&quot;</span> = ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร (BAAC)</div>
        <div><span className="font-semibold">&quot;01035&quot;</span> = - (EXIM Bank)</div>
        <div><span className="font-semibold">&quot;01030&quot;</span> = ธนาคารออมสิน (GSB)</div>
        <div><span className="font-semibold">&quot;01033&quot;</span> = ธนาคารอาคารสงเคราะห์ (GHB)</div>
        <div><span className="font-semibold">&quot;01066&quot;</span> = ธนาคารอิสลามแห่งประเทศไทย (Islamic Bank)</div>
        <div><span className="font-semibold">&quot;02001&quot;</span> = PromptPay บุคคลธรรมดา</div>
        <div><span className="font-semibold">&quot;02003&quot;</span> = PromptPay บัตรประชาชน / เลขประจำตัวผู้เสียภาษี</div>
        <div><span className="font-semibold">&quot;02004&quot;</span> = PromptPay รหัส E-Wallet</div>
        <div><span className="font-semibold">&quot;03000&quot;</span> = K+ Shop (KBANK), แม่มด (SCB), Be Merchant NextGen (BBL), TTS BizShop (TTB)</div>
        <div><span className="font-semibold">&quot;04000&quot;</span> = True Money Wallet</div>
      </div>
    ),
    required: false,
    note: "*ระบุหากต้องการตรวจสอบประเภทบัญชีผู้รับ",
  },
  {
    key: "checkCondition.checkReceiver.accountNameTH",
    type: "String",
    description: "ชื่อบัญชีผู้รับภาษาไทย",
    example: (
      <div className="space-y-1">
        <div>สมชาย สลิปเช็ค</div>
        <div className="text-red-600">*ห้ามใส่คำนำหน้า</div>
      </div>
    ),
    required: false,
    note: "*ระบุทดต้องการตรวจสอบชื่อบัญชีผู้รับภาษาไทย *ห้ามมีคำว่าคำนำหน้า",
  },
  {
    key: "checkCondition.checkReceiver.accountNameEN",
    type: "String",
    description: "ชื่อบัญชีผู้รับภาษาอังกฤษ",
    example: (
      <div className="space-y-1">
        <div>Somchai Thaislipcheck</div>
        <div className="text-red-600">*ห้ามใส่คำนำหน้า</div>
      </div>
    ),
    required: false,
    note: "*ระบุทดต้องการตรวจสอบชื่อบัญชีผู้รับภาษาอังกฤษ *ห้ามมีคำว่าคำนำหน้า",
  },
  {
    key: "checkCondition.checkReceiver.accountNumber",
    type: "String",
    description: "เลขที่บัญชี",
    example: (
      <div className="space-y-1">
        <div>xxxxx1234</div>
        <div className="text-red-600">*ห้ามเว้นวรรคหรือใส่สัญลักษณ์</div>
      </div>
    ),
    required: false,
    note: "*ระบุหากต้องการตรวจสอบเลขที่บัญชี *ห้ามเว้นวรรคหรือใส่เส้นสลับหน่วย",
  },
  {
    key: "checkCondition.checkAmount",
    type: "Object",
    description: "Object การตรวจสอบจำนวนเงิน",
    example: "{ ... }",
    required: false,
    note: "*ระบุหากต้องการตรวจสอบจำนวนเงินโอน",
  },
  {
    key: "checkCondition.checkAmount.type",
    type: "String",
    description: (
      <div className="space-y-1">
        <div>รูปแบบการตรวจสอบจำนวนเงิน</div>
        <div className="text-red-600">*ระบุรูปแบบการตรวจสอบ</div>
      </div>
    ),
    example: (
      <div className="space-y-1">
        <div><span className="font-semibold">&quot;lte&quot;</span> = น้อยกว่าหรือเท่ากับ</div>
        <div><span className="font-semibold">&quot;eq&quot;</span> = เท่ากับ</div>
        <div><span className="font-semibold">&quot;gte&quot;</span> = มากกว่าหรือเท่ากับ</div>
        <div className="text-red-600">*หากไม่ระบุจะมีค่าเท่ากับ &quot;eq&quot;</div>
      </div>
    ),
    required: false,
  },
  {
    key: "checkCondition.checkAmount.amount",
    type: "Number",
    description: "จำนวนเงิน",
    example: (
      <div className="space-y-1">
        <div>10000</div>
        <div className="text-red-600">*ห้ามใส่คอมม่าและลูกน้ำ</div>
      </div>
    ),
    required: false,
  },
  {
    key: "checkCondition.checkDate",
    type: "Object",
    description: "Object การตรวจสอบวันที่โอน",
    example: "{ ... }",
    required: false,
    note: "*ระบุหากต้องการตรวจสอบวันที่โอน",
  },
  {
    key: "checkCondition.checkDate.type",
    type: "String",
    description: (
      <div className="space-y-1">
        <div>รูปแบบการตรวจสอบวันที่</div>
        <div className="text-red-600">*ระบุรูปแบบการตรวจสอบ</div>
      </div>
    ),
    example: (
      <div className="space-y-1">
        <div><span className="font-semibold">&quot;lte&quot;</span> = น้อยกว่าหรือเท่ากับ</div>
        <div><span className="font-semibold">&quot;eq&quot;</span> = เท่ากับ</div>
        <div><span className="font-semibold">&quot;gte&quot;</span> = มากกว่าหรือเท่ากับ</div>
        <div className="text-red-600">*หากไม่ระบุจะมีค่าเท่ากับ &quot;eq&quot;</div>
      </div>
    ),
    required: false,
  },
  {
    key: "checkCondition.checkDate.date",
    type: "DateISO",
    description: (
      <div className="space-y-1">
        <div>วันที่โอน</div>
        <div className="text-red-600">*ระบุวันที่โอน</div>
      </div>
    ),
    example: (
      <div className="space-y-1">
        <div>&quot;2025-10-05T14:48:00.000Z&quot;</div>
        <div className="text-red-600">*ระบุเป็นเวลา GMT</div>
      </div>
    ),
    required: false,
  },
]

// Component to render request body table
function RequestBodyTable({ data }: { data: any[] }) {
  return (
    <div className="mb-6 w-full">
      <h4 className="text-lg font-bold text-gray-900 mb-3">Request Body (JSON)</h4>
      <div className="w-full overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full min-w-[800px]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                Key
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                Type
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                Description
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                Example
              </th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                Required
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} className="border-b last:border-b-0 hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-mono text-blue-600 break-words">
                  {row.key}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                  {row.type}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  <div>{row.description}</div>
                  {row.note && (
                    <div className="mt-1 text-xs text-gray-500 italic">{row.note}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {typeof row.example === "string" ? (
                    <code className="bg-gray-100 px-2 py-1 rounded">{row.example}</code>
                  ) : (
                    row.example
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {row.required ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============ ENDPOINT COMPONENTS ============

// QR Code Endpoint Content Component
function QRCodeEndpointContent() {
  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }
  
  const requestBodyData = [
    {
      key: "qrCode",
      type: "String",
      description: "รหัส QR Code",
      example: "00410006000001030040220014242082547BPM049885102TH9104xxxx",
      required: true,
    },
    ...getCheckConditionFields("qrCode"),
  ]

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="p-6 max-w-full">
        <EndpointHeader
          title="ตรวจสอบสลิปด้วย QR-Code"
          method="POST"
          endpoint="/api/v3/verify/qr-code"
        />

      {/* Tabs */}
      <TabGroup defaultValue="request" size="lg" color="brand-blue" fullWidth>
        <TabGroupList className="mb-6">
          <TabGroupTrigger value="request">รายละเอียด Request</TabGroupTrigger>
          <TabGroupTrigger value="response">รายละเอียด Response</TabGroupTrigger>
          <TabGroupTrigger value="usage">ตัวอย่างการใช้งาน</TabGroupTrigger>
        </TabGroupList>

        {/* Request Tab Content */}
        <TabGroupContent value="request">
          <div>
            {/* Requirements */}
            <div className="mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-3">เงื่อนไขการใช้งาน</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>จะต้องระบุ API Secret มากับ Header ทุกครั้งที่เรียกใช้งาน API</li>
                <li>สามารถกรอง IP Whitelist ในหน้าจัดการ API ได้</li>
                <li><span className="font-bold">Credits Required: 1 credit per successful request</span> (รายการที่ผิดพลาดจะไม่หักเครดิต)</li>
                <li>หากต้องการตรวจสอบเงื่อนไขในสลิป ให้ส่งข้อมูลเฉพาะ Key ที่ต้องการตรวจสอบเท่านั้น</li>
              </ol>
            </div>

            {/* Request Body Table */}
            <RequestBodyTable data={requestBodyData} />

            {/* Example Request Body - No Condition */}
            <div className="mb-6 w-full">
              <h4 className="text-lg font-bold text-gray-900 mb-3">
                ตัวอย่าง Request Body (JSON) <span className="text-red-600">*แบบไม่มีเงื่อนไข</span>
              </h4>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "xxx-xxxx-xxxxxx"
}`
                    handleCopyToClipboard(code, "Code")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 text-sm text-gray-300 overflow-x-auto">
                  <code>{`{
  "qrCode": "xxx-xxxx-xxxxxx"
}`}</code>
                </pre>
              </div>
            </div>

            {/* Example Request Body - With Condition */}
            <div className="mb-6 w-full">
              <h4 className="text-lg font-bold text-gray-900 mb-3">
                ตัวอย่าง Request Body (JSON) <span className="text-red-600">*แบบมีเงื่อนไข</span>
              </h4>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "xxx-xxxx-xxxxxx",
  "checkCondition": {
    "checkDuplicate": true, // เปิดตรวจ slip ซ้ำ
    "checkReceiver": [ // หากระบุหลายเงื่อนไข หากพบว่าเงื่อนไขใดเงื่อนไขหนึ่งถูกต้อง 
      // ระบบจะตอบกลับว่าสลิปถูกต้องทันที
      { // ตรวจสอบธนาคาร ชื่อบัญชี และ เลขที่บัญชี
        "accountType": "01004", // ระบุเมื่อต้องการตรวจสอบประเภทบัญชี
        "accountNameTH": "สมชาย สลิปปุ้ม", // ระบุเมื่อต้องการตรวจสอบชื่อบัญชีภาษาไทย
        "accountNameEN": "Somchai Thaislipcheck", // ระบุเมื่อต้องการตรวจสอบชื่อบัญชีภาษาอังกฤษ
        "accountNumber": "xxxxxx1234" // ระบุเมื่อต้องการตรวจสอบเลขที่บัญชี
      },
      { // ตรวจสอบเฉพาะเลขที่บัญชี
        "accountNumber": "xxxxxx1234"
      },
      { // ตรวจสอบเฉพาะชื่อบัญชี
        "accountNameTH": "สมชาย สลิปเช็ค",
        "accountNameEN": "Somchai Thaislipcheck",
      }
    ],
    "checkAmount": {
      "type": "eq", // eq, gte, lte
      "amount": 10000
    },
    "checkDate": {
      "type": "eq", // eq, gte, lte
      "date": "2025-10-05T14:48:00.000Z" // GMT time
    }
  }
}`
                    handleCopyToClipboard(code, "Code")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "xxx-xxxx-xxxxxx",
  "checkCondition": {
    "checkDuplicate": true, // เปิดตรวจ slip ซ้ำ
    "checkReceiver": [ // หากระบุหลายเงื่อนไข หากพบว่าเงื่อนไขใดเงื่อนไขหนึ่งถูกต้อง 
      // ระบบจะตอบกลับว่าสลิปถูกต้องทันที
      { // ตรวจสอบธนาคาร ชื่อบัญชี และ เลขที่บัญชี
        "accountType": "01004", // ระบุเมื่อต้องการตรวจสอบประเภทบัญชี
        "accountNameTH": "สมชาย สลิปปุ้ม", // ระบุเมื่อต้องการตรวจสอบชื่อบัญชีภาษาไทย
        "accountNameEN": "Somchai Thaislipcheck", // ระบุเมื่อต้องการตรวจสอบชื่อบัญชีภาษาอังกฤษ
        "accountNumber": "xxxxxx1234" // ระบุเมื่อต้องการตรวจสอบเลขที่บัญชี
      },
      { // ตรวจสอบเฉพาะเลขที่บัญชี
        "accountNumber": "xxxxxx1234"
      },
      { // ตรวจสอบเฉพาะชื่อบัญชี
        "accountNameTH": "สมชาย สลิปเช็ค",
        "accountNameEN": "Somchai Thaislipcheck",
      }
    ],
    "checkAmount": {
      "type": "eq", // eq, gte, lte
      "amount": 10000
    },
    "checkDate": {
      "type": "eq", // eq, gte, lte
      "date": "2025-10-05T14:48:00.000Z" // GMT time
    }
  }
}`}</code>
                </pre>
              </div>
            </div>
          </div>
        </TabGroupContent>

        {/* Response Tab Content */}
        <TabGroupContent value="response">
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-3">รายละเอียด Response</h4>
            
            {/* Response Table */}
            <div className="mb-6 w-full">
              <div className="w-full overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                        Key
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                        Example
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">code</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสผลลัพธ์ของการทำรายการ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;200000&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">message</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อความผลลัพธ์ของการทำรายการ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;Verification completed successfully&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลลัพธ์ข้อมูลทั้งหมด</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.referenceId</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสอ้างอิงสลิป</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;92887bd5-60d3-4744-9a98-b8574eaxxxx-xx&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.decode</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสถอดรหัส QR Code</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;00020101021129370016A000000677010112011420024280529130049685010371440641070707&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.transRef</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสอ้างอิงของธนาคารผู้โอน</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;015073144014FAT0099Y&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.dateTime</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันและเวลาที่โอน</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;2024-05-29T05:37:00.000Z&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.amount</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">number</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงินที่โอน</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">100.00</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref1</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสอ้างอิง 1</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;xxx&quot; | null</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref2</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสอ้างอิง 2</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;xxx&quot; | null</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref3</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสอ้างอิง 3</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;xxx&quot; | null</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลบัญชีผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.name</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ชื่อบัญชีผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;บริษัท สลิปเช็ค จำกัด&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.bank</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลธนาคารผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.bank.account</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขบัญชีธนาคารผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;xxxx-x-5366-x&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.proxy</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | NULL</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลตัวแทนบัญชีผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... } | null'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.proxy.type</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">NATID, MSISDN, EWALLETID, EMAIL, BILLERID</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;NATID&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.proxy.account</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขตัวแทนบัญชีผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;xxxx-x-5366-x&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.bank</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลธนาคารผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.bank.id</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขธนาคารผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;004&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.bank.name</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ชื่อธนาคารผู้รับ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;ธนาคารกสิกรไทย&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลผู้ส่ง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.account</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลบัญชีผู้ส่ง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.account.name</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ชื่อผู้ส่ง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;บริษัท สลิปเช็ค จำกัด&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.account.bank</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลบัญชีผู้ส่ง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.account.bank.account</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขที่บัญชีผู้ส่ง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;xxxx-x-9866-x&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.bank</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลธนาคารผู้ส่ง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.bank.id</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขธนาคารผู้ส่ง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;004&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.bank.name</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ชื่อธนาคารผู้ส่ง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;ธนาคารกสิกรไทย&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลการตรวจสอบเงื่อนไข (มีเฉพาะเมื่อส่ง checkCondition)</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.duplicate</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลตรวจสอบสลิปซ้ำ</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.duplicate.passed</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผ่านการตรวจสอบสลิปซ้ำหรือไม่</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.receiver</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลตรวจสอบผู้รับเงิน</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.receiver.passed</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผ่านการตรวจสอบผู้รับหรือไม่</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.amount</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลตรวจสอบจำนวนเงิน</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.amount.passed</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผ่านการตรวจสอบจำนวนเงินหรือไม่</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.amount.expectedAmount</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงินที่คาดหวัง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">100</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.amount.actualAmount</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงินจริง</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">100</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.date</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลตรวจสอบวันที่</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.date.passed</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผ่านการตรวจสอบวันที่หรือไม่</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.date.expectedDate</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันที่คาดหวัง (ISO 8601)</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;2025-10-05T14:48:00.000Z&quot;</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.date.actualDate</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันที่จริง (ISO 8601)</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;2024-05-29T05:37:00.000Z&quot;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Example Response */}
            <div className="mb-6 w-full">
              <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Response</h4>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "code": "200000",
  "message": "Verification completed successfully",
  "data": {
    "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
    "decode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
    "transRef": "015073144014FAT0099Y",
    "dateTime": "2024-05-29T05:37:00.000Z",
    "amount": 1,
    "ref1": null,
    "ref2": null,
    "ref3": null,
    "receiver": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxxx-x-5366-x"
        },
        "proxy": {
          "type": "NATID",
          "account": "xxx-x-x5366-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "sender": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxx-x-9866-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "conditionResults": {
      "duplicate": { "passed": true, "reason": "No duplicate found" },
      "receiver": { "passed": true, "matchedConditionIndex": 0 },
      "amount": { "passed": true, "expectedAmount": 1, "actualAmount": 1 },
      "date": { "passed": true, "expectedDate": "2025-10-05T14:48:00.000Z", "actualDate": "2024-05-29T05:37:00.000Z" }
    }
  }
}`
                    handleCopyToClipboard(code, "Response")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "code": "200000",
  "message": "Verification completed successfully",
  "data": {
    "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
    "decode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
    "transRef": "015073144014FAT0099Y",
    "dateTime": "2024-05-29T05:37:00.000Z",
    "amount": 1,
    "ref1": null,
    "ref2": null,
    "ref3": null,
    "receiver": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxxx-x-5366-x"
        },
        "proxy": {
          "type": "NATID",
          "account": "xxx-x-x5366-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "sender": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxx-x-9866-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "conditionResults": {
      "duplicate": { "passed": true, "reason": "No duplicate found" },
      "receiver": { "passed": true, "matchedConditionIndex": 0 },
      "amount": { "passed": true, "expectedAmount": 1, "actualAmount": 1 },
      "date": { "passed": true, "expectedDate": "2025-10-05T14:48:00.000Z", "actualDate": "2024-05-29T05:37:00.000Z" }
    }
  }
}`}</code>
                </pre>
              </div>
            </div>
          </div>
        </TabGroupContent>

        {/* Usage Tab Content */}
        <TabGroupContent value="usage">
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่างการใช้งาน</h4>

            {/* JavaScript Example */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">วิธีการใช้งาน JavaScript</h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `const axios = require('axios')

let config = {
  method: 'POST',
  maxBodyLength: Infinity,
  url: 'https://slipaj.com/api/v3/verify/qr-code',
  headers: {
    'Authorization': 'Bearer {secretKey}'
  },
  data: {
    "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx"
  }
}

axios.request(config).then((response) => {
  console.log(JSON.stringify(response.data))
}).catch((error) => {
  console.log(error)
})`
                    handleCopyToClipboard(code, "JavaScript Code")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`const axios = require('axios')

let config = {
  method: 'POST',
  maxBodyLength: Infinity,
  url: 'https://slipaj.com/api/v3/verify/qr-code',
  headers: {
    'Authorization': 'Bearer {secretKey}'
  },
  data: {
    "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx"
  }
}

axios.request(config).then((response) => {
  console.log(JSON.stringify(response.data))
}).catch((error) => {
  console.log(error)
})`}</code>
                </pre>
              </div>
            </div>

            {/* cURL Example */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">วิธีการใช้งาน Curl</h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `curl -iL -X POST 'https://slipaj.com/api/v3/verify/qr-code' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer {secretKey}' \\
  -d '{"qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx"}'`
                    handleCopyToClipboard(code, "cURL Code")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`curl -iL -X POST 'https://slipaj.com/api/v3/verify/qr-code' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer {secretKey}' \\
  -d '{"qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx"}'`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 1 - No Condition */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบสลิปโดยไม่มีเงื่อนไข
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx"
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx"
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 2 - With Duplicate Check */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                แบบมีเช็คสลิปซ้ำ
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkDuplicate": true
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkDuplicate": true
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 3 - With Receiver Check */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบประเภทบัญชีผู้รับ <span className="text-red-600">*ตรวจสอบประเภทบัญชีผู้รับ</span>
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "01004"
      }
    ]
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "01004"
      }
    ]
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 4 - Check Account Name */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบชื่อบัญชี
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountNameTH": "สวัสดี บริษัทฯ",
        "accountNameEN": "Sawatdee PhaiJun"
      }
    ]
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountNameTH": "สวัสดี บริษัทฯ",
        "accountNameEN": "Sawatdee PhaiJun"
      }
    ]
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 5 - Check Amount */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบยอดเงินโอน
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkAmount": {
      "type": "eq",
      "amount": 10000
    }
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkAmount": {
      "type": "eq",
      "amount": 10000
    }
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 6 - Check Date */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบวันที่โอน
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkDate": {
      "type": "eq",
      "date": "2025-04-23T03:32:43:123Z"
    }
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkDate": {
      "type": "eq",
      "date": "2025-04-23T03:32:43:123Z"
    }
  }
}`}</code>
                </pre>
              </div>
            </div>

            <h4 className="text-lg font-bold text-gray-900 mb-3">ตรวจสลิปโดยเช็คบัญชีผู้รับตามเงื่อนไขที่ต้องการ</h4>

            {/* Response Example 7 - Check Bank Account Type and Number */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบบัญชีกับรหัสบัญชี (01004 = ธนาคารกสิกรไทย)
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 8 - Check PromptPay */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบ PromptPay และรหัส PromptPay (02001 = แบบรหัสโทรศัพท์)
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "02001",
        "accountNumber": "0802358994"
      }
    ]
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "02001",
        "accountNumber": "0802358994"
      }
    ]
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 9 - Check Merchant */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบ Merchant และรหัสร้านค้า (เช่น Kshop, แม่มด)
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "03000",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "qrCode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "03000",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 10 - Check TrueMoney Wallet */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบบัญชี TrueMoney ด้วยรหัส Wallet
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "04000",
          "accountNumber": "0802358994"
        }
      ]
    }
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "04000",
          "accountNumber": "0802358994"
        }
      ]
    }
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 11 - Check Bank with Account Name */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบประเภทบัญชีพร้อมชื่อและเลขบัญชี
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun"
        }
      ]
    }
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun"
        }
      ]
    }
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example 12 - Check Bank with Full Details */}
            <div className="mb-6 w-full">
              <h5 className="text-base font-semibold text-gray-900 mb-3">
                ตรวจสอบประเภทบัญชี, ชื่อบัญชี และเลขที่บัญชี
              </h5>
              <div className="relative bg-gray-800 rounded-lg">
                <button
                  onClick={() => {
                    const code = `{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "420-185366-6"
        }
      ]
    }
  }
}`
                    handleCopyToClipboard(code, "Request")
                  }}
                  className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy code"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "420-185366-6"
        }
      ]
    }
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Example 13: Check account type, names, number, and amount */}
            <div className="mb-6 w-full">
              <h4 className="font-semibold text-gray-900 mb-3">
                ตรวจสลิปโดยเช็คประเภทบัญชี, ชื่อบัญชี, เลขที่บัญชี, ยอดเงินโอน
              </h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "420-185366-6"
        }
      ],
      "checkAmount": {
        "type": "eq",
        "amount": 10000
      }
    }
  }
}`, 'ตัวอย่าง')}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                  title="คัดลอก"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "420-185366-6"
        }
      ],
      "checkAmount": {
        "type": "eq",
        "amount": 10000
      }
    }
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Example 14: Check account type, names, number, amount, and date */}
            <div className="mb-6 w-full">
              <h4 className="font-semibold text-gray-900 mb-3">
                ตรวจสลิปโดยเช็คประเภทบัญชี, ชื่อบัญชี, เลขที่บัญชี, ยอดเงินโอน และวันที่โอน
              </h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "420-185366-6"
        }
      ],
      "checkAmount": {
        "type": "eq",
        "amount": 10000
      },
      "checkDate": {
        "type": "eq",
        "date": "2025-04-23T08:32:45.123Z"
      }
    }
  }
}`, 'ตัวอย่าง')}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                  title="คัดลอก"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "420-185366-6"
        }
      ],
      "checkAmount": {
        "type": "eq",
        "amount": 10000
      },
      "checkDate": {
        "type": "eq",
        "date": "2025-04-23T08:32:45.123Z"
      }
    }
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Example 15: Check duplicate with account details */}
            <div className="mb-6 w-full">
              <h4 className="font-semibold text-gray-900 mb-3">
                ตรวจสอบเฉพาะบัญชีโอนไปเงินโอน และเช็คสลิปซ้ำ
              </h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkDuplicate": true,
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "420-185366-6"
        }
      ],
      "checkAmount": {
        "type": "eq",
        "amount": 10000
      }
    }
  }
}`, 'ตัวอย่าง')}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                  title="คัดลอก"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkDuplicate": true,
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "420-185366-6"
        }
      ],
      "checkAmount": {
        "type": "eq",
        "amount": 10000
      }
    }
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Example 16: Check multiple receiver conditions */}
            <div className="mb-6 w-full">
              <h4 className="font-semibold text-gray-900 mb-3">
                ตรวจสอบสลิปครอบคลึงตัวแปรหลายประเภท (หากเจอบัญชีใดบัญชีหนึ่งที่ตรงตัวกรองถือว่า Pass)
              </h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountNumber": "7152477419"
        },
        {
          "accountType": "01004",
          "accountNumber": "0884950374"
        },
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun"
        },
        {
          "accountType": "01001",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "8173123441"
        }
      ]
    }
  }
}`, 'ตัวอย่าง')}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                  title="คัดลอก"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkReceiver": [
        {
          "accountNumber": "7152477419"
        },
        {
          "accountType": "01004",
          "accountNumber": "0884950374"
        },
        {
          "accountType": "01004",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun"
        },
        {
          "accountType": "01001",
          "accountNameTH": "สวัสดี จันทรคณะ",
          "accountNameEN": "SawatDee WanJun",
          "accountNumber": "8173123441"
        }
      ]
    }
  }
}`}</code>
                </pre>
              </div>
            </div>

            {/* Example 17: Check account type and number only (no names) */}
            <div className="mb-6 w-full">
              <h4 className="font-semibold text-gray-900 mb-3">
                ข้อมูลเช็คเฉพาะไปไม่ถูกต้อง (accountType ไม่ถูกต้อง)
              </h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkDuplicate": true,
      "checkReceiver": [
        {
          "accountType": "03a0690",
          "accountNumber": "420-185366-6"
        }
      ]
    }
  }
}`, 'ตัวอย่าง')}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                  title="คัดลอก"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`{
  "payload": {
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAASABIAAD...",
    "checkCondition": {
      "checkDuplicate": true,
      "checkReceiver": [
        {
          "accountType": "03a0690",
          "accountNumber": "420-185366-6"
        }
      ]
    }
  }
}`}</code>
                </pre>
              </div>
            </div>
          </div>
        </TabGroupContent>
      </TabGroup>
      </div>
    </div>
  )
}

// ============ IMAGE ENDPOINT COMPONENT ============
function ImageEndpointContent() {
  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  const requestBodyData = [
    {
      key: "image",
      type: "File",
      description: "ไฟล์รูปภาพสลิป",
      example: "slip.jpg",
      required: true,
    },
    {
      key: "payload",
      type: "String (JSON)",
      description: "JSON String ข้อมูลที่ต้องการตรวจสอบเงื่อนไข",
      example: '{"checkCondition": {...}}',
      required: false,
    },
    ...getCheckConditionFields("image"),
  ]

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="p-6 max-w-full">
        <EndpointHeader
          title="ตรวจสอบสลิปด้วยรูปภาพ"
          method="POST"
          endpoint="/api/v3/verify/image"
        />

        <TabGroup defaultValue="request" size="lg" color="brand-blue" fullWidth>
          <TabGroupList className="mb-6">
            <TabGroupTrigger value="request">รายละเอียด Request</TabGroupTrigger>
            <TabGroupTrigger value="response">รายละเอียด Response</TabGroupTrigger>
            <TabGroupTrigger value="usage">ตัวอย่างการใช้งาน</TabGroupTrigger>
          </TabGroupList>

          <TabGroupContent value="request">
            <div>
              {/* Requirements */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">เงื่อนไขการใช้งาน</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>จะต้องระบุ API Secret มากับ Header ทุกครั้งที่เรียกใช้งาน API</li>
                  <li><span className="font-bold">Credits Required: 1 credit per successful request</span> (รายการที่ผิดพลาดจะไม่หักเครดิต)</li>
                  <li>ส่งไฟล์รูปภาพผ่าน multipart/form-data</li>
                  <li>Form Field: <code className="bg-gray-100 px-2 py-1 rounded">image</code> (ไฟล์รูปภาพ)</li>
                  <li>Form Field: <code className="bg-gray-100 px-2 py-1 rounded">payload</code> (JSON string สำหรับ checkCondition - optional)</li>
                  <li>Supported Types: JPEG, PNG, WebP | Max Size: 10 MB</li>
                </ol>
              </div>

              {/* Request Body Table */}
              <RequestBodyTable data={requestBodyData} />
            </div>
          </TabGroupContent>

          <TabGroupContent value="response">
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">รายละเอียด Response</h4>
              
              {/* Response Table */}
              <div className="mb-6 w-full">
                <div className="w-full overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                          Key
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                          Example
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">code</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสผลลัพธ์ของการทำรายการ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;200000&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">message</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อความผลลัพธ์ของการทำรายการ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;Verification completed successfully&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลลัพธ์ข้อมูลทั้งหมด</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.referenceId</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสอ้างอิงสลิป</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;92887bd5-60d3-4744-9a98-b8574eaxxxx-xx&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.decode</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">QR Code ที่ถอดรหัสได้</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;00410006000001...&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.transRef</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขที่อ้างอิงธนาคาร</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;015073144014FAT0099Y&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.dateTime</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String (ISO 8601)</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันเวลาที่ทำรายการ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;2024-05-29T05:37:00.000Z&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.amount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงินที่โอน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">1</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref1</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 1</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">null</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref2</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 2</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">null</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref3</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 3</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">null</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลบัญชีผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.name</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ชื่อผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;บริษัท สลิปเช็ค จำกัด&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.bank</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลบัญชีผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.bank.account</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขที่บัญชีผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;xxxx-x-5366-x&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.proxy</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล Proxy Account</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.proxy.type</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ประเภท Proxy</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;NATID&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.account.proxy.account</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขตัวแทนบัญชีผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;xxxx-x-5366-x&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.bank</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลธนาคารผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.bank.id</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขธนาคารผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;004&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver.bank.name</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ชื่อธนาคารผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;ธนาคารกสิกรไทย&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.account</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลบัญชีผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.account.name</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ชื่อผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;บริษัท สลิปเช็ค จำกัด&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.account.bank</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลบัญชีผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.account.bank.account</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขที่บัญชีผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;xxxx-x-9866-x&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.bank</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลธนาคารผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.bank.id</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขธนาคารผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;004&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender.bank.name</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ชื่อธนาคารผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;ธนาคารกสิกรไทย&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลการตรวจสอบเงื่อนไข (มีเฉพาะเมื่อส่ง checkCondition)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.duplicate</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลตรวจสอบสลิปซ้ำ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.duplicate.passed</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผ่านการตรวจสอบสลิปซ้ำหรือไม่</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.receiver</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลตรวจสอบผู้รับเงิน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.receiver.passed</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผ่านการตรวจสอบผู้รับหรือไม่</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.amount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลตรวจสอบจำนวนเงิน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.amount.passed</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผ่านการตรวจสอบจำนวนเงินหรือไม่</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.amount.expectedAmount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงินที่คาดหวัง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">100</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.amount.actualAmount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงินจริง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">100</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.date</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลตรวจสอบวันที่</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.date.passed</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผ่านการตรวจสอบวันที่หรือไม่</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.date.expectedDate</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันที่คาดหวัง (ISO 8601)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;2025-10-05T14:48:00.000Z&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults.date.actualDate</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันที่จริง (ISO 8601)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;2024-05-29T05:37:00.000Z&quot;</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example Response */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Response</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "code": "200000",
  "message": "Verification completed successfully",
  "data": {
    "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
    "decode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
    "transRef": "015073144014FAT0099Y",
    "dateTime": "2024-05-29T05:37:00.000Z",
    "amount": 1,
    "ref1": null,
    "ref2": null,
    "ref3": null,
    "receiver": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxxx-x-5366-x"
        },
        "proxy": {
          "type": "NATID",
          "account": "xxx-x-x5366-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "sender": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxx-x-9866-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "conditionResults": {
      "duplicate": { "passed": true, "reason": "No duplicate found" },
      "receiver": { "passed": true, "matchedConditionIndex": 0 },
      "amount": { "passed": true, "expectedAmount": 1, "actualAmount": 1 },
      "date": { "passed": true, "expectedDate": "2025-10-05T14:48:00.000Z", "actualDate": "2024-05-29T05:37:00.000Z" }
    }
  }
}`
                      handleCopyToClipboard(code, "Response")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "code": "200000",
  "message": "Verification completed successfully",
  "data": {
    "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
    "decode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
    "transRef": "015073144014FAT0099Y",
    "dateTime": "2024-05-29T05:37:00.000Z",
    "amount": 1,
    "ref1": null,
    "ref2": null,
    "ref3": null,
    "receiver": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxxx-x-5366-x"
        },
        "proxy": {
          "type": "NATID",
          "account": "xxx-x-x5366-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "sender": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxx-x-9866-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "conditionResults": {
      "duplicate": { "passed": true, "reason": "No duplicate found" },
      "receiver": { "passed": true, "matchedConditionIndex": 0 },
      "amount": { "passed": true, "expectedAmount": 1, "actualAmount": 1 },
      "date": { "passed": true, "expectedDate": "2025-10-05T14:48:00.000Z", "actualDate": "2024-05-29T05:37:00.000Z" }
    }
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="usage">
            <div>
              {/* cURL Example */}
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={"checkCondition":{"checkDuplicate":true}}'`,
                    'cURL'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={"checkCondition":{"checkDuplicate":true}}'`}</code>
                </pre>
              </div>

              <h4 className="text-lg font-bold text-gray-900 mb-6 mt-6">JavaScript Example</h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `const formData = new FormData();
formData.append('image', fileInput.files[0]);
formData.append('payload', JSON.stringify({
  checkCondition: { checkDuplicate: true }
}));

const response = await fetch('https://slipaj.com/api/v3/verify/image', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: formData
});

const result = await response.json();`,
                    'JavaScript'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`const formData = new FormData();
formData.append('image', fileInput.files[0]);
formData.append('payload', JSON.stringify({
  checkCondition: { checkDuplicate: true }
}));

const response = await fetch('https://slipaj.com/api/v3/verify/image', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: formData
});

const result = await response.json();`}</code>
                </pre>
              </div>

              {/* Request Body Examples */}
              <h4 className="text-lg font-bold text-gray-900 mb-3 mt-8">ตัวอย่าง Request Body</h4>
              
              {/* Example 1 - No Condition */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบสลิปโดยไม่มีเงื่อนไข
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `# แค่อัพโหลดไฟล์รูปภาพ ไม่ต้องส่ง payload
curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg"`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`# แค่อัพโหลดไฟล์รูปภาพ ไม่ต้องส่ง payload
curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg"`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 2 - With Duplicate Check */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  แบบมีเช็คสลิปซ้ำ
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={"checkCondition":{"checkDuplicate":true}}'`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={"checkCondition":{"checkDuplicate":true}}'`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 3 - Check Amount */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบยอดเงินโอน
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={"checkCondition":{"checkAmount":{"type":"eq","amount":10000}}}'`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={"checkCondition":{"checkAmount":{"type":"eq","amount":10000}}}'`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 4 - Check Receiver Account */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบบัญชีผู้รับ
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={"checkCondition":{"checkReceiver":[{"accountType":"01004","accountNumber":"420-185366-6"}]}}'`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={"checkCondition":{"checkReceiver":[{"accountType":"01004","accountNumber":"420-185366-6"}]}}'`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 5 - Multiple Conditions */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบหลายเงื่อนไขพร้อมกัน
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={
    "checkCondition": {
      "checkDuplicate": true,
      "checkAmount": {
        "type": "gte",
        "amount": 1000
      },
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNumber": "420-185366-6"
        }
      ]
    }
  }'`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`curl -X POST https://slipaj.com/api/v3/verify/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@/path/to/slip.jpg" \\
  -F 'payload={
    "checkCondition": {
      "checkDuplicate": true,
      "checkAmount": {
        "type": "gte",
        "amount": 1000
      },
      "checkReceiver": [
        {
          "accountType": "01004",
          "accountNumber": "420-185366-6"
        }
      ]
    }
  }'`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>
        </TabGroup>
      </div>
    </div>
  )
}

// ============ BASE64 ENDPOINT COMPONENT ============
function Base64EndpointContent() {
  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  const requestBodyData = [
    {
      key: "imageBase64",
      type: "String",
      description: "รูปภาพสลิปในรูปแบบ Base64 String",
      example: "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
      required: true,
    },
    ...getCheckConditionFields("imageBase64"),
  ]

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="p-6 max-w-full">
        <EndpointHeader
          title="ตรวจสอบสลิปด้วย Base64"
          method="POST"
          endpoint="/api/v3/verify/base64"
        />

        <TabGroup defaultValue="request" size="lg" color="brand-blue" fullWidth>
          <TabGroupList className="mb-6">
            <TabGroupTrigger value="request">รายละเอียด Request</TabGroupTrigger>
            <TabGroupTrigger value="response">รายละเอียด Response</TabGroupTrigger>
            <TabGroupTrigger value="usage">ตัวอย่างการใช้งาน</TabGroupTrigger>
          </TabGroupList>

          <TabGroupContent value="request">
            <div>
              {/* Requirements */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">เงื่อนไขการใช้งาน</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>จะต้องระบุ API Secret มากับ Header ทุกครั้งที่เรียกใช้งาน API</li>
                  <li><span className="font-bold">Credits Required: 1 credit per successful request</span> (รายการที่ผิดพลาดจะไม่หักเครดิต)</li>
                  <li>ส่งรูปภาพในรูปแบบ Base64 String (รองรับทั้ง Data URI และ Base64 String)</li>
                  <li>รูปแบบ Data URI: <code className="bg-gray-100 px-2 py-1 rounded">data:image/jpeg;base64,...</code></li>
                  <li>หากต้องการตรวจสอบเงื่อนไข ให้ส่งข้อมูลเฉพาะ Key ที่ต้องการตรวจสอบเท่านั้น</li>
                </ol>
              </div>

              {/* Request Body Table */}
              <RequestBodyTable data={requestBodyData} />
            </div>
          </TabGroupContent>

          <TabGroupContent value="response">
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">รายละเอียด Response</h4>
              
              {/* Response Table */}
              <div className="mb-6 w-full">
                <div className="w-full overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                          Key
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b whitespace-nowrap">
                          Example
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">code</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสผลลัพธ์ของการทำรายการ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;200000&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">message</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อความผลลัพธ์ของการทำรายการ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;Verification completed successfully&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลลัพธ์ข้อมูลทั้งหมด</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.referenceId</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสอ้างอิงสลิป</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;92887bd5-60d3-4744-9a98-b8574eaxxxx-xx&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.decode</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">QR Code ที่ถอดรหัสได้</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;00410006000001...&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.transRef</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เลขที่อ้างอิงธนาคาร</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;015073144014FAT0099Y&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.dateTime</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String (ISO 8601)</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันเวลาที่ทำรายการ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&quot;2024-05-29T05:37:00.000Z&quot;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.amount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงินที่โอน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">1</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref1</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 1</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">null</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref2</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 2</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">null</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref3</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 3</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">null</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลการตรวจสอบเงื่อนไข (มีเฉพาะเมื่อส่ง checkCondition)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example Response */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Response</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "code": "200000",
  "message": "Verification completed successfully",
  "data": {
    "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
    "decode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
    "transRef": "015073144014FAT0099Y",
    "dateTime": "2024-05-29T05:37:00.000Z",
    "amount": 1,
    "ref1": null,
    "ref2": null,
    "ref3": null,
    "receiver": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxxx-x-5366-x"
        },
        "proxy": {
          "type": "NATID",
          "account": "xxx-x-x5366-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "sender": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxx-x-9866-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "conditionResults": {
      "duplicate": { "passed": true, "reason": "No duplicate found" },
      "receiver": { "passed": true, "matchedConditionIndex": 0 },
      "amount": { "passed": true, "expectedAmount": 1, "actualAmount": 1 },
      "date": { "passed": true, "expectedDate": "2025-10-05T14:48:00.000Z", "actualDate": "2024-05-29T05:37:00.000Z" }
    }
  }
}`
                      handleCopyToClipboard(code, "Response")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "code": "200000",
  "message": "Verification completed successfully",
  "data": {
    "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
    "decode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
    "transRef": "015073144014FAT0099Y",
    "dateTime": "2024-05-29T05:37:00.000Z",
    "amount": 1,
    "ref1": null,
    "ref2": null,
    "ref3": null,
    "receiver": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxxx-x-5366-x"
        },
        "proxy": {
          "type": "NATID",
          "account": "xxx-x-x5366-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "sender": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxx-x-9866-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "conditionResults": {
      "duplicate": { "passed": true, "reason": "No duplicate found" },
      "receiver": { "passed": true, "matchedConditionIndex": 0 },
      "amount": { "passed": true, "expectedAmount": 1, "actualAmount": 1 },
      "date": { "passed": true, "expectedDate": "2025-10-05T14:48:00.000Z", "actualDate": "2024-05-29T05:37:00.000Z" }
    }
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="usage">
            <div>
              {/* cURL Example */}
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `curl -X POST https://slipaj.com/api/v3/verify/base64 \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
    "checkCondition": { "checkDuplicate": true }
  }'`,
                    'cURL'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`curl -X POST https://slipaj.com/api/v3/verify/base64 \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
    "checkCondition": { "checkDuplicate": true }
  }'`}</code>
                </pre>
              </div>

              <h4 className="text-lg font-bold text-gray-900 mb-6 mt-6">JavaScript Example</h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `const axios = require('axios');

// Convert image to base64
const imageBase64 = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...";

const response = await axios.post('https://slipaj.com/api/v3/verify/base64', {
  imageBase64: imageBase64,
  checkCondition: {
    checkDuplicate: true
  }
}, {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

console.log(response.data);`,
                    'JavaScript'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`const axios = require('axios');

// Convert image to base64
const imageBase64 = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...";

const response = await axios.post('https://slipaj.com/api/v3/verify/base64', {
  imageBase64: imageBase64,
  checkCondition: {
    checkDuplicate: true
  }
}, {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

console.log(response.data);`}</code>
                </pre>
              </div>

              {/* Request Body Examples */}
              <h4 className="text-lg font-bold text-gray-900 mb-3 mt-8">ตัวอย่าง Request Body</h4>
              
              {/* Example 1 - No Condition */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบสลิปโดยไม่มีเงื่อนไข
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD..."
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD..."
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 2 - With Duplicate Check */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  แบบมีเช็คสลิปซ้ำ
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
  "checkCondition": {
    "checkDuplicate": true
  }
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
  "checkCondition": {
    "checkDuplicate": true
  }
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 3 - Check Amount */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบยอดเงินโอน
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
  "checkCondition": {
    "checkAmount": {
      "type": "eq",
      "amount": 10000
    }
  }
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
  "checkCondition": {
    "checkAmount": {
      "type": "eq",
      "amount": 10000
    }
  }
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 4 - Check Receiver */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบบัญชีผู้รับ
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 5 - Multiple Conditions */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบหลายเงื่อนไขพร้อมกัน
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
  "checkCondition": {
    "checkDuplicate": true,
    "checkAmount": {
      "type": "gte",
      "amount": 1000
    },
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD...",
  "checkCondition": {
    "checkDuplicate": true,
    "checkAmount": {
      "type": "gte",
      "amount": 1000
    },
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>
        </TabGroup>
      </div>
    </div>
  )
}

// ============ URL ENDPOINT COMPONENT ============
function URLEndpointContent() {
  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  const requestBodyData = [
    {
      key: "imageUrl",
      type: "String",
      description: "URL ของรูปภาพสลิป (ต้องเป็น public URL)",
      example: "https://example.com/slip.jpg",
      required: true,
    },
    ...getCheckConditionFields("imageUrl"),
  ]

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="p-6 max-w-full">
        <EndpointHeader
          title="ตรวจสอบสลิปด้วย URL"
          method="POST"
          endpoint="/api/v3/verify/url"
        />

        <TabGroup defaultValue="request" size="lg" color="brand-blue" fullWidth>
          <TabGroupList className="mb-6">
            <TabGroupTrigger value="request">รายละเอียด Request</TabGroupTrigger>
            <TabGroupTrigger value="response">รายละเอียด Response</TabGroupTrigger>
            <TabGroupTrigger value="usage">ตัวอย่างการใช้งาน</TabGroupTrigger>
          </TabGroupList>

          <TabGroupContent value="request">
            <div>
              {/* Requirements */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">เงื่อนไขการใช้งาน</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>จะต้องระบุ API Secret มากับ Header ทุกครั้งที่เรียกใช้งาน API</li>
                  <li>URL ของรูปภาพต้องเป็น Public URL ที่สามารถเข้าถึงได้จากภายนอก</li>
                  <li>รองรับ JPEG, PNG, WebP</li>
                  <li>หากต้องการตรวจสอบเงื่อนไข ให้ส่งข้อมูลเฉพาะ Key ที่ต้องการตรวจสอบเท่านั้น</li>
                  <li>Credits Required: 1 credit per successful request (failures do not deduct credits)</li>
                </ol>
              </div>

              {/* Request Body Table */}
              <RequestBodyTable data={requestBodyData} />
            </div>
          </TabGroupContent>

          <TabGroupContent value="response">
            <div>
              {/* Response Fields Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">รายละเอียด Response</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Field</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">code</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสสถานะ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"200000"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">message</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อความอธิบายสถานะ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"Verification completed successfully"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลสลิป</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.referenceId</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสอ้างอิงเฉพาะของสลิป</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"92887bd5-..."</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.decode</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล QR Code ที่ถอดรหัสแล้ว</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"00410006..."</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.transRef</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิงธุรกรรม</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"015073144..."</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.dateTime</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String (ISO 8601)</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันเวลาที่ทำธุรกรรม</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"2024-05-29T05:37:00.000Z"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.amount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงิน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">1</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref1</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 1</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">null</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref2</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 2</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">null</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.ref3</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | NULL</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 3</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">null</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.receiver</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลผู้รับ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.sender</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลผู้ส่ง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.conditionResults</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object | undefined</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ผลการตรวจสอบเงื่อนไข (มีเฉพาะเมื่อส่ง checkCondition)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{"{ ... }"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example Response */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Response</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "code": "200000",
  "message": "Verification completed successfully",
  "data": {
    "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
    "decode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
    "transRef": "015073144014FAT0099Y",
    "dateTime": "2024-05-29T05:37:00.000Z",
    "amount": 1,
    "ref1": null,
    "ref2": null,
    "ref3": null,
    "receiver": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxxx-x-5366-x"
        },
        "proxy": {
          "type": "NATID",
          "account": "xxx-x-x5366-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "sender": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxx-x-9866-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "conditionResults": {
      "duplicate": { "passed": true, "reason": "No duplicate found" },
      "receiver": { "passed": true, "matchedConditionIndex": 0 },
      "amount": { "passed": true, "expectedAmount": 1, "actualAmount": 1 },
      "date": { "passed": true, "expectedDate": "2025-10-05T14:48:00.000Z", "actualDate": "2024-05-29T05:37:00.000Z" }
    }
  }
}`
                      handleCopyToClipboard(code, "Response")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "code": "200000",
  "message": "Verification completed successfully",
  "data": {
    "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
    "decode": "00410006000001030040220014242082547BPM049885102TH9104xxxx",
    "transRef": "015073144014FAT0099Y",
    "dateTime": "2024-05-29T05:37:00.000Z",
    "amount": 1,
    "ref1": null,
    "ref2": null,
    "ref3": null,
    "receiver": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxxx-x-5366-x"
        },
        "proxy": {
          "type": "NATID",
          "account": "xxx-x-x5366-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "sender": {
      "account": {
        "name": "บริษัท สลิปเช็ค จำกัด",
        "bank": {
          "account": "xxx-x-9866-x"
        }
      },
      "bank": {
        "id": "004",
        "name": "ธนาคารกสิกรไทย"
      }
    },
    "conditionResults": {
      "duplicate": { "passed": true, "reason": "No duplicate found" },
      "receiver": { "passed": true, "matchedConditionIndex": 0 },
      "amount": { "passed": true, "expectedAmount": 1, "actualAmount": 1 },
      "date": { "passed": true, "expectedDate": "2025-10-05T14:48:00.000Z", "actualDate": "2024-05-29T05:37:00.000Z" }
    }
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="usage">
            <div>
              {/* cURL Example */}
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `curl -X POST https://slipaj.com/api/v3/verify/url \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "imageUrl": "https://example.com/slip.jpg",
    "checkCondition": { "checkDuplicate": true }
  }'`,
                    'cURL'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`curl -X POST https://slipaj.com/api/v3/verify/url \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "imageUrl": "https://example.com/slip.jpg",
    "checkCondition": { "checkDuplicate": true }
  }'`}</code>
                </pre>
              </div>

              <h4 className="text-lg font-bold text-gray-900 mb-6 mt-6">JavaScript Example</h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `const axios = require('axios');

const response = await axios.post('https://slipaj.com/api/v3/verify/url', {
  imageUrl: 'https://example.com/slip.jpg',
  checkCondition: {
    checkDuplicate: true
  }
}, {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

console.log(response.data);`,
                    'JavaScript'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`const axios = require('axios');

const response = await axios.post('https://slipaj.com/api/v3/verify/url', {
  imageUrl: 'https://example.com/slip.jpg',
  checkCondition: {
    checkDuplicate: true
  }
}, {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

console.log(response.data);`}</code>
                </pre>
              </div>

              {/* Request Body Examples */}
              <h4 className="text-lg font-bold text-gray-900 mb-3 mt-8">ตัวอย่าง Request Body</h4>
              
              {/* Example 1 - No Condition */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบสลิปโดยไม่มีเงื่อนไข
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageUrl": "https://example.com/slip.jpg"
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageUrl": "https://example.com/slip.jpg"
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 2 - With Duplicate Check */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  แบบมีเช็คสลิปซ้ำ
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageUrl": "https://example.com/slip.jpg",
  "checkCondition": {
    "checkDuplicate": true
  }
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageUrl": "https://example.com/slip.jpg",
  "checkCondition": {
    "checkDuplicate": true
  }
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 3 - Check Amount */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบยอดเงินโอน
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageUrl": "https://example.com/slip.jpg",
  "checkCondition": {
    "checkAmount": {
      "type": "eq",
      "amount": 10000
    }
  }
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageUrl": "https://example.com/slip.jpg",
  "checkCondition": {
    "checkAmount": {
      "type": "eq",
      "amount": 10000
    }
  }
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 4 - Check Receiver */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบบัญชีผู้รับ
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageUrl": "https://example.com/slip.jpg",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageUrl": "https://example.com/slip.jpg",
  "checkCondition": {
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 5 - Multiple Conditions */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตรวจสอบหลายเงื่อนไขพร้อมกัน
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "imageUrl": "https://example.com/slip.jpg",
  "checkCondition": {
    "checkDuplicate": true,
    "checkAmount": {
      "type": "gte",
      "amount": 1000
    },
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "imageUrl": "https://example.com/slip.jpg",
  "checkCondition": {
    "checkDuplicate": true,
    "checkAmount": {
      "type": "gte",
      "amount": 1000
    },
    "checkReceiver": [
      {
        "accountType": "01004",
        "accountNumber": "420-185366-6"
      }
    ]
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>
        </TabGroup>
      </div>
    </div>
  )
}

// ============ PROMPTPAY ENDPOINT COMPONENT ============
function PromptPayEndpointContent() {
  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="p-6 max-w-full">
        <EndpointHeader
          title="สร้าง PromptPay QR Code"
          method="POST"
          endpoint="/api/v3/generate/promptpay"
        />

        <TabGroup defaultValue="request" size="lg" color="brand-blue" fullWidth>
          <TabGroupList className="mb-6">
            <TabGroupTrigger value="request">รายละเอียด Request</TabGroupTrigger>
            <TabGroupTrigger value="response">รายละเอียด Response</TabGroupTrigger>
            <TabGroupTrigger value="usage">ตัวอย่างการใช้งาน</TabGroupTrigger>
          </TabGroupList>

          <TabGroupContent value="request">
            <div>
              {/* Requirements */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">เงื่อนไขการใช้งาน</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>จะต้องระบุ API Secret มากับ Header ทุกครั้งที่เรียกใช้งาน API</li>
                  <li>ต้องระบุ accountType และ accountId ทั้งคู่ (required)</li>
                  <li>accountType รองรับ: phone, nationalId, taxId, ewallet</li>
                  <li>amount เป็น optional (ถ้าต้องการฝังจำนวนเงินใน QR)</li>
                  <li>Credits Required: 1 credit per successful request</li>
                </ol>
              </div>

              {/* Request Body Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Request Body Parameters</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Parameter</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Required</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">accountType</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">✅ Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-700">ประเภทบัญชี: phone, nationalId, taxId, ewallet</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"phone"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">accountId</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">✅ Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-700">หมายเลขบัญชี (ขึ้นอยู่กับ accountType เช่น เบอร์โทร / เลขบัตร)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"0812345678"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">amount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">No</td>
                        <td className="px-4 py-3 text-sm text-gray-700">จำนวนเงิน (บาท, optional — ถ้าไม่ระบุจะสร้าง QR แบบไม่ระบุยอด)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">1000</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Example */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Request Body</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "accountType": "phone",
  "accountId": "0812345678",
  "amount": 1000
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "accountType": "phone",
  "accountId": "0812345678",
  "amount": 1000
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="response">
            <div>
              {/* Response Fields Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">รายละเอียด Response</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Field</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">code</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสสถานะ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"200100"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">message</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อความอธิบายสถานะ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"QR code generated successfully"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล QR Code</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.qrId</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสอ้างอิงที่ generate ไว้ใช้ตรวจสอบภายหลัง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"qr_abc123..."</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.qrType</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ประเภท QR Code</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"promptpay"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.format</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รูปแบบไ༝ล์: png, svg, dataurl</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"png"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.qrData</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ไฟล์ QR Code (base64 สำหรับ png / SVG string สำหรับ svg / Data URL)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"iVBORw0KGgo..."</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.payload</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล QR Code ในรูปแบบ EMVCo payload string</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"00020101021129..."</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.accountInfo</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">สรุปข้อมูลบัญชี (accountId ถูก mask ไว้)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ accountType, accountId }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.amount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number | null</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงินที่ระบุ (null ถ้าไม่ได้ระบุ)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">1000</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.timestamp</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันและเวลาที่ generate (ISO 8601)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"2025-01-30T10:00:00.000Z"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.credits</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">สรุปการหักเครดิต</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ used: 1, breakdown: { generation: 1 } }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล Metadata ของ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta.processingTime</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เวลาในการประมวลผล Request (มิลลิวินาที)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">12</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example Response */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Response</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "code": "200100",
  "message": "QR code generated successfully",
  "data": {
    "qrId": "qr_abc123def456",
    "qrType": "promptpay",
    "format": "png",
    "qrData": "iVBORw0KGgoAAAANSUhEUgAA...",
    "payload": "00020101021129370016A000000677010111011300668123456785802TH5303764540510.006304A1B2",
    "accountInfo": {
      "accountType": "phone",
      "accountId": "0812***"
    },
    "amount": 1000,
    "timestamp": "2025-01-30T10:00:00.000Z",
    "credits": {
      "used": 1,
      "breakdown": { "generation": 1 }
    }
  },
  "meta": {
    "processingTime": 12
  }
}`
                      handleCopyToClipboard(code, "Response")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "code": "200100",
  "message": "QR code generated successfully",
  "data": {
    "qrId": "qr_abc123def456",
    "qrType": "promptpay",
    "format": "png",
    "qrData": "iVBORw0KGgoAAAANSUhEUgAA...",
    "payload": "00020101021129370016A000000677010111011300668123456785802TH5303764540510.006304A1B2",
    "accountInfo": {
      "accountType": "phone",
      "accountId": "0812***"
    },
    "amount": 1000,
    "timestamp": "2025-01-30T10:00:00.000Z",
    "credits": {
      "used": 1,
      "breakdown": { "generation": 1 }
    }
  },
  "meta": {
    "processingTime": 12
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="usage">
            <div>
              {/* cURL Example */}
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `curl -X POST https://slipaj.com/api/v3/generate/promptpay \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "accountType": "phone",
    "accountId": "0812345678",
    "amount": 1000
  }'`,
                    'cURL'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`curl -X POST https://slipaj.com/api/v3/generate/promptpay \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "accountType": "phone",
    "accountId": "0812345678",
    "amount": 1000
  }'`}</code>
                </pre>
              </div>

              <h4 className="text-lg font-bold text-gray-900 mb-6 mt-6">JavaScript Example</h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `const axios = require('axios');

const response = await axios.post('https://slipaj.com/api/v3/generate/promptpay', {
  accountType: 'phone',
  accountId: '0812345678',
  amount: 1000
}, {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

console.log(response.data.data.qrData);`,
                    'JavaScript'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`const axios = require('axios');

const response = await axios.post('https://slipaj.com/api/v3/generate/promptpay', {
  accountType: 'phone',
  accountId: '0812345678',
  amount: 1000
}, {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

console.log(response.data.data.qrData);`}</code>
                </pre>
              </div>

              {/* Request Body Examples */}
              <h4 className="text-lg font-bold text-gray-900 mb-3 mt-8">ตัวอย่าง Request Body</h4>
              
              {/* Example 1 - Mobile Number */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  สร้าง QR ด้วยเบอร์โทรศัพท์ (accountType: phone)
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "accountType": "phone",
  "accountId": "0812345678",
  "amount": 1000
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "accountType": "phone",
  "accountId": "0812345678",
  "amount": 1000
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 2 - National ID */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  สร้าง QR ด้วยเลขบัตรประชาชน (accountType: nationalId)
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "accountType": "nationalId",
  "accountId": "1234567890123",
  "amount": 1500
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "accountType": "nationalId",
  "accountId": "1234567890123",
  "amount": 1500
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 3 - With ref1 */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  สร้าง QR ไม่ระบุยอดเงิน (QR แบบ open amount)
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "accountType": "phone",
  "accountId": "0812345678"
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "accountType": "phone",
  "accountId": "0812345678"
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 4 - With both refs */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  สร้าง QR ด้วย ewallet ID
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "accountType": "ewallet",
  "accountId": "ewallet123456",
  "amount": 500
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "accountType": "ewallet",
  "accountId": "ewallet123456",
  "amount": 500
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 5 - Large amount */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  สร้าง QR ด้วย taxId
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "accountType": "taxId",
  "accountId": "1234567890123",
  "amount": 99999.99
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "accountType": "taxId",
  "accountId": "1234567890123",
  "amount": 99999.99
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>
        </TabGroup>
      </div>
    </div>
  )
}

// ============ BILL PAYMENT ENDPOINT COMPONENT ============
function BillPaymentEndpointContent() {
  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="p-6 max-w-full">
        <EndpointHeader
          title="สร้าง Bill Payment QR Code"
          method="POST"
          endpoint="/api/v3/generate/bill-payment"
        />

        <TabGroup defaultValue="request" size="lg" color="brand-blue" fullWidth>
          <TabGroupList className="mb-6">
            <TabGroupTrigger value="request">รายละเอียด Request</TabGroupTrigger>
            <TabGroupTrigger value="response">รายละเอียด Response</TabGroupTrigger>
            <TabGroupTrigger value="usage">ตัวอย่างการใช้งาน</TabGroupTrigger>
          </TabGroupList>

          <TabGroupContent value="request">
            <div>
              {/* Requirements */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">เงื่อนไขการใช้งาน</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>จะต้องระบุ API Secret มากับ Header ทุกครั้งที่เรียกใช้งาน API</li>
                  <li>ต้องระบุ billerId (รหัสบิลเลอร์) ที่ถูกต้อง</li>
                  <li>ต้องระบุ reference1 เสมอ (required)</li>
                  <li>amount เป็น optional (ถ้าต้องการฝังยอดใน QR)</li>
                  <li>Credits Required: 1 credit per successful request</li>
                </ol>
              </div>

              {/* Request Body Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Request Body Parameters</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Parameter</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Required</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">billerId</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">✅ Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-700">รหัสบิลเลอร์ (Biller ID) ความยาว 10-15 อักขระ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"010570016761234"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">reference1</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">✅ Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-700">หมายเลขอ้างอิง 1 (required, max 20 อักขระ)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"BILL001"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">reference2</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">No</td>
                        <td className="px-4 py-3 text-sm text-gray-700">หมายเลขอ้างอิง 2 (optional, max 20 อักขระ)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"CUST123"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">amount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">No</td>
                        <td className="px-4 py-3 text-sm text-gray-700">จำนวนเงิน (บาท, optional — ถ้าไม่ระบุจะสร้าง QR แบบไม่ระบุยอด)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">5000</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Example */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Request Body</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "billerId": "010570016761234",
  "reference1": "BILL001",
  "reference2": "CUST123",
  "amount": 5000
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "billerId": "010570016761234",
  "reference1": "BILL001",
  "reference2": "CUST123",
  "amount": 5000
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="response">
            <div>
              {/* Response Fields Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">รายละเอียด Response</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Field</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">code</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสสถานะ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"200100"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">message</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อความอธิบายสถานะ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"QR code generated successfully"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล QR Code</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.qrId</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">UUID ของ QR ที่สร้าง</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"qr_9876543210"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.qrType</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ประเภท QR Code</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"bill-payment"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.format</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รูปแบบไฟล์ QR</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"png"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.qrData</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Base64 ของรูป QR Code</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">&lt;base64&gt;</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.payload</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">QR payload string (EMVCo)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"000201..."</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.billerId</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสบิลเลอร์</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"010570016761234"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.reference1</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 1</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"BILL001"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.reference2</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String | null</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หมายเลขอ้างอิง 2</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"CUST123"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.amount</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number | null</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนเงิน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">5000</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.timestamp</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เวลาที่สร้าง QR (ISO 8601)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"2025-01-04T09:42:49.000Z"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.credits</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เครดิตที่ใช้</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{`{used:1}`}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล Metadata ของ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta.processingTime</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เวลาในการประมวลผล Request (มิลลิวินาที)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">10</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example Response */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Response</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "code": "200100",
  "message": "QR code generated successfully",
  "data": {
    "qrId": "qr_9876543210_xyz789",
    "qrType": "bill-payment",
    "format": "png",
    "qrData": "<base64-encoded-png-image>",
    "payload": "000201010212300105700167612340306BILL00106CUST1235802TH6304XXXX",
    "billerId": "010570016761234",
    "reference1": "BILL001",
    "reference2": "CUST123",
    "amount": 5000,
    "timestamp": "2025-01-04T09:42:49.000Z",
    "credits": {
      "used": 1,
      "breakdown": { "generation": 1 }
    }
  },
  "meta": {
    "processingTime": 10
  }
}`
                      handleCopyToClipboard(code, "Response")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "code": "200100",
  "message": "QR code generated successfully",
  "data": {
    "qrId": "qr_9876543210_xyz789",
    "qrType": "bill-payment",
    "format": "png",
    "qrData": "<base64-encoded-png-image>",
    "payload": "000201010212300105700167612340306BILL00106CUST1235802TH6304XXXX",
    "billerId": "010570016761234",
    "reference1": "BILL001",
    "reference2": "CUST123",
    "amount": 5000,
    "timestamp": "2025-01-04T09:42:49.000Z",
    "credits": {
      "used": 1,
      "breakdown": { "generation": 1 }
    }
  },
  "meta": {
    "processingTime": 10
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="usage">
            <div>
              {/* cURL Example */}
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `curl -X POST https://slipaj.com/api/v3/generate/bill-payment \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "billerId": "010570016761234",
    "reference1": "BILL001",
    "amount": 5000
  }'`,
                    'cURL'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`curl -X POST https://slipaj.com/api/v3/generate/bill-payment \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "billerId": "010570016761234",
    "reference1": "BILL001",
    "amount": 5000
  }'`}</code>
                </pre>
              </div>

              <h4 className="text-lg font-bold text-gray-900 mb-6 mt-6">JavaScript Example</h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `const axios = require('axios');

const response = await axios.post('https://slipaj.com/api/v3/generate/bill-payment', {
  billerId: '010570016761234',
  reference1: 'BILL001',
  reference2: 'CUST123',
  amount: 5000
}, {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

console.log(response.data.data.qrCode);`,
                    'JavaScript'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`const axios = require('axios');

const response = await axios.post('https://slipaj.com/api/v3/generate/bill-payment', {
  billerId: '010570016761234',
  reference1: 'BILL001',
  reference2: 'CUST123',
  amount: 5000
}, {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

console.log(response.data.data.qrCode);`}</code>
                </pre>
              </div>

              {/* Request Body Examples */}
              <h4 className="text-lg font-bold text-gray-900 mb-3 mt-8">ตัวอย่าง Request Body</h4>
              
              {/* Example 1 - Basic */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  สร้าง Bill Payment QR แบบพื้นฐาน
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "billerId": "010570016761234",
  "amount": 5000
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "billerId": "010570016761234",
  "amount": 5000
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 2 - With ref1 */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  สร้าง QR พร้อมหมายเลขอ้างอิง 1 ตัว
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "billerId": "010570016761234",
  "reference1": "ELEC202501",
  "amount": 8500
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "billerId": "010570016761234",
  "reference1": "ELEC202501",
  "amount": 8500
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 3 - With both refs */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  สร้าง QR พร้อมหมายเลขอ้างอิง 2 ตัว
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "billerId": "010570016761234",
  "reference1": "WATER202501",
  "reference2": "CUSTOMER-001",
  "amount": 12000
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "billerId": "010570016761234",
  "reference1": "WATER202501",
  "reference2": "CUSTOMER-001",
  "amount": 12000
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 4 - Utility Bill */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ตัวอย่างบิลค่าสาธารณูปโภค
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "billerId": "099400012345678",
  "reference1": "MEA-JAN2025",
  "reference2": "ACCT1234567890",
  "amount": 3250.75
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "billerId": "099400012345678",
  "reference1": "MEA-JAN2025",
  "reference2": "ACCT1234567890",
  "amount": 3250.75
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 5 - Large Amount */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  สร้าง QR บิลยอดสูง
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "billerId": "010570016761234",
  "reference1": "PREMIUM-BILL-2025",
  "reference2": "CORP-CLIENT-999",
  "amount": 99999.99
}`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "billerId": "010570016761234",
  "reference1": "PREMIUM-BILL-2025",
  "reference2": "CORP-CLIENT-999",
  "amount": 99999.99
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>
        </TabGroup>
      </div>
    </div>
  )
}

// ============ HISTORY ENDPOINT COMPONENT ============
function HistoryEndpointContent() {
  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="p-6 max-w-full">
        <EndpointHeader
          title="ดึงข้อมูลสลิปเก่า"
          method="GET"
          endpoint="/api/v3/history/verifications"
        />

        <TabGroup defaultValue="request" size="lg" color="brand-blue" fullWidth>
          <TabGroupList className="mb-6">
            <TabGroupTrigger value="request">รายละเอียด Request</TabGroupTrigger>
            <TabGroupTrigger value="response">รายละเอียด Response</TabGroupTrigger>
            <TabGroupTrigger value="usage">ตัวอย่างการใช้งาน</TabGroupTrigger>
          </TabGroupList>

          <TabGroupContent value="request">
            <div>
              {/* Requirements */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">เงื่อนไขการใช้งาน</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>จะต้องระบุ API Secret มากับ Header ทุกครั้งที่เรียกใช้งาษ API</li>
                  <li>Query parameters ทั้งหมดเป็น optional</li>
                  <li>สามารถกรองตามวันที่ด้วย startDate และ endDate</li>
                  <li>Credits Required: 0 credits (ฟรี)</li>
                </ol>
              </div>

              {/* Query Parameters Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Query Parameters</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Parameter</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Required</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">limit</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">No</td>
                        <td className="px-4 py-3 text-sm text-gray-700">จำนวนสลิปต่อหน้า (default: 10, max: 100)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">20</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">page</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">No</td>
                        <td className="px-4 py-3 text-sm text-gray-700">หมายเลขหน้า (default: 1)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">1</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">startDate</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">No</td>
                        <td className="px-4 py-3 text-sm text-gray-700">วันเริ่มต้น (YYYY-MM-DD)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"2025-01-01"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">endDate</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">No</td>
                        <td className="px-4 py-3 text-sm text-gray-700">วันสิ้นสุด (YYYY-MM-DD)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"2025-12-31"</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Example */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Request</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `GET /api/v3/history/verifications?limit=20&page=1&startDate=2025-01-01&endDate=2025-12-31
Headers:
  Authorization: Bearer YOUR_API_KEY`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`GET /api/v3/history/verifications?limit=20&page=1&startDate=2025-01-01&endDate=2025-12-31
Headers:
  Authorization: Bearer YOUR_API_KEY`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="response">
            <div>
              {/* Response Fields Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">รายละเอียด Response</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Field</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">code</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสสถานะ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"200200"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">message</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อความอธิบายสถานะ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"History retrieved successfully"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลประวัติสลิป</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.history</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Array</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รายการสลิปทั้งหมด</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">[...]</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.pagination</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล Pagination</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.pagination.total</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนสลิปทั้งหมด</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">100</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.pagination.limit</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนต่อหน้า</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">20</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.pagination.currentPage</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">หน้าปัจจุบัน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">1</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.pagination.totalPages</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวนหน้าทั้งหมด</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">5</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.pagination.hasNextPage</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">มีหน้าถัดไปหรือไม่</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.pagination.hasPreviousPage</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">มีหน้าก่อนหน้าหรือไม่</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">false</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล Metadata ของ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta.processingTime</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เวลาในการประมวลผล Request (มิลลิวินาที)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">25</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example Response */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Response</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "code": "200200",
  "message": "History retrieved successfully",
  "data": {
    "history": [
      {
        "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
        "amount": 1000,
        "transRef": "015073144014FAT0099Y",
        "dateTime": "2024-05-29T05:37:00.000Z",
        "receiver": {
          "account": { "name": "บริษัท สลิปเช็ค จำกัด" },
          "bank": { "id": "004", "name": "ธนาคารกสิกรไทย" }
        },
        "sender": {
          "account": { "name": "นาย ทดสอบ ระบบ" },
          "bank": { "id": "004", "name": "ธนาคารกสิกรไทย" }
        },
        "createdAt": "2024-05-29T05:38:00.000Z"
      }
    ],
    "pagination": {
      "total": 100,
      "limit": 20,
      "currentPage": 1,
      "totalPages": 5,
      "hasNextPage": true,
      "hasPreviousPage": false
    }
  },
  "meta": {
    "processingTime": 25
  }
}`
                      handleCopyToClipboard(code, "Response")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "code": "200200",
  "message": "History retrieved successfully",
  "data": {
    "history": [
      {
        "referenceId": "92887bd5-60d3-4744-9a98-b8574eaxxxx-xx",
        "amount": 1000,
        "transRef": "015073144014FAT0099Y",
        "dateTime": "2024-05-29T05:37:00.000Z",
        "receiver": {
          "account": { "name": "บริษัท สลิปเช็ค จำกัด" },
          "bank": { "id": "004", "name": "ธนาคารกสิกรไทย" }
        },
        "sender": {
          "account": { "name": "นาย ทดสอบ ระบบ" },
          "bank": { "id": "004", "name": "ธนาคารกสิกรไทย" }
        },
        "createdAt": "2024-05-29T05:38:00.000Z"
      }
    ],
    "pagination": {
      "total": 100,
      "limit": 20,
      "currentPage": 1,
      "totalPages": 5,
      "hasNextPage": true,
      "hasPreviousPage": false
    }
  },
  "meta": {
    "processingTime": 25
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="usage">
            <div>
              {/* cURL Example */}
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `curl -X GET "https://slipaj.com/api/v3/history/verifications?limit=20&page=1" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
                    'cURL'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`curl -X GET "https://slipaj.com/api/v3/history/verifications?limit=20&page=1" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</code>
                </pre>
              </div>

              <h4 className="text-lg font-bold text-gray-900 mb-6 mt-6">JavaScript Example</h4>
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleCopyToClipboard(
                    `const axios = require('axios');

const params = {
  limit: 20,
  page: 1,
  startDate: '2025-01-01',
  endDate: '2025-12-31'
};

const response = await axios.get('https://slipaj.com/api/v3/history/verifications', {
  params: params,
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  }
});

console.log(response.data.data.history);`,
                    'JavaScript'
                  )}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10 bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{`const axios = require('axios');

const params = {
  limit: 20,
  page: 1,
  startDate: '2025-01-01',
  endDate: '2025-12-31'
};

const response = await axios.get('https://slipaj.com/api/v3/history/verifications', {
  params: params,
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  }
});

console.log(response.data.data.history);`}</code>
                </pre>
              </div>

              {/* Request Examples */}
              <h4 className="text-lg font-bold text-gray-900 mb-3 mt-8">ตัวอย่างการใช้งาน</h4>
              
              {/* Example 1 - Basic */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ดึงประวัติสลิปแบบพื้นฐาน (10 รายการล่าสุด)
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `GET /api/v3/history/verifications`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`GET /api/v3/history/verifications`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 2 - With Pagination */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ดึงสลิป 50 รายการ
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `GET /api/v3/history/verifications?limit=50`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`GET /api/v3/history/verifications?limit=50`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 3 - With Date Range */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ดึงสลิปตามช่วงวันที่
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `GET /api/v3/history/verifications?startDate=2025-01-01&endDate=2025-01-31`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`GET /api/v3/history/verifications?startDate=2025-01-01&endDate=2025-01-31`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 4 - Pagination with Offset */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ดึงสลิปหน้าถัดไป (Pagination)
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `GET /api/v3/history/verifications?limit=20&page=2`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`GET /api/v3/history/verifications?limit=20&page=2`}</code>
                  </pre>
                </div>
              </div>

              {/* Example 5 - Complete */}
              <div className="mb-6 w-full">
                <h5 className="text-base font-semibold text-gray-900 mb-3">
                  ดึงสลิปแบบครบถ้วน (ระบุทุก parameter)
                </h5>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `GET /api/v3/history/verifications?limit=30&page=1&startDate=2025-01-01&endDate=2025-12-31`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`GET /api/v3/history/verifications?limit=30&page=1&startDate=2025-01-01&endDate=2025-12-31`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>
        </TabGroup>
      </div>
    </div>
  )
}

// ============ ACCOUNT ENDPOINT COMPONENT ============
function AccountEndpointContent() {
  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="p-6 max-w-full">
        <EndpointHeader
          title="ดึงข้อมูลบัญชี"
          method="GET"
          endpoint="/api/v3/account/info"
        />

        <TabGroup defaultValue="request" size="lg" color="brand-blue" fullWidth>
          <TabGroupList className="mb-6">
            <TabGroupTrigger value="request">รายละเอียด Request</TabGroupTrigger>
            <TabGroupTrigger value="response">รายละเอียด Response</TabGroupTrigger>
            <TabGroupTrigger value="usage">ตัวอย่างการใช้งาน</TabGroupTrigger>
          </TabGroupList>

          <TabGroupContent value="request">
            <div>
              {/* Requirements */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">เงื่อนไขการใช้งาน</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>จะต้องระบุ API Secret มากับ Header ทุกครั้งที่เรียกใช้งาน API</li>
                  <li>ไม่มี query parameters หรือ request body</li>
                  <li>เพียงส่ง GET request พร้อม Authorization header</li>
                  <li>Credits Required: 0 credits (ฟรี)</li>
                </ol>
              </div>

              {/* Request Example */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Request</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `GET /api/v3/account/info
Headers:
  Authorization: Bearer YOUR_API_KEY`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`GET /api/v3/account/info
Headers:
  Authorization: Bearer YOUR_API_KEY`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="response">
            <div>
              {/* Response Fields Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">รายละเอียด Response</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Field</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">code</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสสถานะ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"200300"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">message</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อความอธิบายสถานะ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"Account information retrieved"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลบัญชี</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.account</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลบัญชีผู้ใช้</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.account.id</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสบัญชี</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"acc-001"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.account.businessName</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ชื่อธุรกิจ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"บริษัท ABC จำกัด"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.account.creditLimit</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วงเงิน Credit ทั้งหมด</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">100000</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.account.creditUsed</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Credit ที่ใช้ไปแล้ว</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">50000</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.account.creditRemaining</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Credit คงเหลือ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">50000</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.account.plan</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">แผนการใช้งาน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"Pro"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.account.isActive</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Boolean</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">สถานะบัญชี</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">true</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.account.createdAt</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String (ISO 8601)</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">วันที่สร้างบัญชี</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"2025-01-01T00:00:00.000Z"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล Metadata ของ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta.processingTime</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เวลาในการประมวลผล Request (มิลลิวินาที)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">8</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example Response */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Response</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "code": "200300",
  "message": "Account information retrieved",
  "data": {
    "account": {
      "id": "acc-001",
      "businessName": "บริษัท ABC จำกัด",
      "creditLimit": 100000,
      "creditUsed": 50000,
      "creditRemaining": 50000,
      "plan": "Pro",
      "isActive": true,
      "createdAt": "2025-01-01T00:00:00.000Z"
    }
  },
  "meta": {
    "processingTime": 8
  }
}`
                      handleCopyToClipboard(code, "Response")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "code": "200300",
  "message": "Account information retrieved",
  "data": {
    "account": {
      "id": "acc-001",
      "businessName": "บริษัท ABC จำกัด",
      "creditLimit": 100000,
      "creditUsed": 50000,
      "creditRemaining": 50000,
      "plan": "Pro",
      "isActive": true,
      "createdAt": "2025-01-01T00:00:00.000Z"
    }
  },
  "meta": {
    "processingTime": 8
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="usage">
            <div>
              {/* cURL Example */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">1. ตัวอย่างการใช้งานด้วย cURL</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `curl -X GET "https://slipaj.com/api/v3/account/info" \\
  -H "Authorization: Bearer YOUR_API_KEY"`
                      handleCopyToClipboard(code, "cURL")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`curl -X GET "https://slipaj.com/api/v3/account/info" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</code>
                  </pre>
                </div>
              </div>

              {/* JavaScript/Axios Example */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">2. ตัวอย่างการใช้งานด้วย JavaScript (axios)</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `const axios = require('axios');

const getAccountInfo = async () => {
  try {
    const response = await axios.get('https://slipaj.com/api/v3/account/info', {
      headers: {
        'Authorization': 'Bearer YOUR_API_KEY'
      }
    });

    if (response.data.code === '200300') {
      const account = response.data.data.account;
      console.log('บัญชี:', account.businessName);
      console.log('Credit คงเหลือ:', account.creditRemaining);
      console.log('แผน:', account.plan);
      console.log('เวลาประมวลผล:', response.data.meta.processingTime, 'ms');
    }
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
  }
};

getAccountInfo();`
                      handleCopyToClipboard(code, "JavaScript")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`const axios = require('axios');

const getAccountInfo = async () => {
  try {
    const response = await axios.get('https://slipaj.com/api/v3/account/info', {
      headers: {
        'Authorization': 'Bearer YOUR_API_KEY'
      }
    });

    if (response.data.code === '200300') {
      const account = response.data.data.account;
      console.log('บัญชี:', account.businessName);
      console.log('Credit คงเหลือ:', account.creditRemaining);
      console.log('แผน:', account.plan);
      console.log('เวลาประมวลผล:', response.data.meta.processingTime, 'ms');
    }
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
  }
};

getAccountInfo();`}</code>
                  </pre>
                </div>
              </div>

              {/* Scenario Examples */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">3. ตัวอย่างการใช้งานในสถานการณ์ต่างๆ</h4>

                {/* Scenario 1 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 1: ตรวจสอบยอด Credit คงเหลือ</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// ตรวจสอบ credit ก่อนทำงาน
const response = await axios.get('https://slipaj.com/api/v3/account/info', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const creditRemaining = response.data.data.account.creditRemaining;

if (creditRemaining < 100) {
  console.log('⚠️ Credit เหลือน้อย กรุณาเติม Credit');
} else {
  console.log('✓ Credit เพียงพอสำหรับการใช้งาน:', creditRemaining);
}`
                        handleCopyToClipboard(code, "Scenario 1")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// ตรวจสอบ credit ก่อนทำงาน
const response = await axios.get('https://slipaj.com/api/v3/account/info', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const creditRemaining = response.data.data.account.creditRemaining;

if (creditRemaining < 100) {
  console.log('⚠️ Credit เหลือน้อย กรุณาเติม Credit');
} else {
  console.log('✓ Credit เพียงพอสำหรับการใช้งาน:', creditRemaining);
}`}</code>
                    </pre>
                  </div>
                </div>

                {/* Scenario 2 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 2: แสดงข้อมูลบัญชีใน Dashboard</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// แสดงข้อมูลบัญชีในหน้า Dashboard
const response = await axios.get('https://slipaj.com/api/v3/account/info', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const account = response.data.data.account;

// แสดงข้อมูลสำคัญ
console.log('ชื่อธุรกิจ:', account.businessName);
console.log('แผนการใช้งาน:', account.plan);
console.log('Credit ใช้ไป:', account.creditUsed.toLocaleString());
console.log('Credit คงเหลือ:', account.creditRemaining.toLocaleString());
console.log('Credit ทั้งหมด:', account.creditLimit.toLocaleString());

// คำนวณเปอร์เซ็นต์การใช้งาน
const usagePercent = (account.creditUsed / account.creditLimit * 100).toFixed(2);
console.log('การใช้งาน:', usagePercent + '%');`
                        handleCopyToClipboard(code, "Scenario 2")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// แสดงข้อมูลบัญชีในหน้า Dashboard
const response = await axios.get('https://slipaj.com/api/v3/account/info', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const account = response.data.data.account;

// แสดงข้อมูลสำคัญ
console.log('ชื่อธุรกิจ:', account.businessName);
console.log('แผนการใช้งาน:', account.plan);
console.log('Credit ใช้ไป:', account.creditUsed.toLocaleString());
console.log('Credit คงเหลือ:', account.creditRemaining.toLocaleString());
console.log('Credit ทั้งหมด:', account.creditLimit.toLocaleString());

// คำนวณเปอร์เซ็นต์การใช้งาน
const usagePercent = (account.creditUsed / account.creditLimit * 100).toFixed(2);
console.log('การใช้งาน:', usagePercent + '%');`}</code>
                    </pre>
                  </div>
                </div>

                {/* Scenario 3 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 3: ตรวจสอบสถานะบัญชีก่อนเรียก API อื่น</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// ตรวจสอบสถานะบัญชีก่อนทำงาน
const response = await axios.get('https://slipaj.com/api/v3/account/info', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const account = response.data.data.account;

// ตรวจสอบสถานะ
if (!account.isActive) {
  throw new Error('บัญชีถูกปิดการใช้งาน กรุณาติดต่อฝ่ายสนับสนุน');
}

// ตรวจสอบ credit
if (account.creditRemaining <= 0) {
  throw new Error('Credit หมด กรุณาเติม Credit');
}

console.log('✓ บัญชีพร้อมใช้งาน สามารถเรียก API ได้');`
                        handleCopyToClipboard(code, "Scenario 3")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// ตรวจสอบสถานะบัญชีก่อนทำงาน
const response = await axios.get('https://slipaj.com/api/v3/account/info', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const account = response.data.data.account;

// ตรวจสอบสถานะ
if (!account.isActive) {
  throw new Error('บัญชีถูกปิดการใช้งาน กรุณาติดต่อฝ่ายสนับสนุน');
}

// ตรวจสอบ credit
if (account.creditRemaining <= 0) {
  throw new Error('Credit หมด กรุณาเติม Credit');
}

console.log('✓ บัญชีพร้อมใช้งาน สามารถเรียก API ได้');`}</code>
                    </pre>
                  </div>
                </div>

                {/* Scenario 4 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 4: สร้าง Monitoring Alert เมื่อ Credit ต่ำ</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// ติดตาม credit และส่ง alert
const monitorCredit = async () => {
  const response = await axios.get('https://slipaj.com/api/v3/account/info', {
    headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
  });

  const account = response.data.data.account;
  const percentage = (account.creditRemaining / account.creditLimit) * 100;

  if (percentage < 10) {
    // ส่ง Email/SMS Alert
    console.log('🚨 ALERT: Credit เหลือน้อยมาก (' + percentage.toFixed(2) + '%)');
    await sendAlert({
      level: 'critical',
      message: \`Credit เหลือเพียง \${account.creditRemaining} (\${percentage.toFixed(2)}%)\`
    });
  } else if (percentage < 25) {
    console.log('⚠️ WARNING: Credit เหลือน้อย (' + percentage.toFixed(2) + '%)');
  } else {
    console.log('✓ Credit ปกติ (' + percentage.toFixed(2) + '%)');
  }
};

// เรียกใช้งานทุกชั่วโมง
setInterval(monitorCredit, 60 * 60 * 1000);`
                        handleCopyToClipboard(code, "Scenario 4")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// ติดตาม credit และส่ง alert
const monitorCredit = async () => {
  const response = await axios.get('https://slipaj.com/api/v3/account/info', {
    headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
  });

  const account = response.data.data.account;
  const percentage = (account.creditRemaining / account.creditLimit) * 100;

  if (percentage < 10) {
    // ส่ง Email/SMS Alert
    console.log('🚨 ALERT: Credit เหลือน้อยมาก (' + percentage.toFixed(2) + '%)');
    await sendAlert({
      level: 'critical',
      message: \`Credit เหลือเพียง \${account.creditRemaining} (\${percentage.toFixed(2)}%)\`
    });
  } else if (percentage < 25) {
    console.log('⚠️ WARNING: Credit เหลือน้อย (' + percentage.toFixed(2) + '%)');
  } else {
    console.log('✓ Credit ปกติ (' + percentage.toFixed(2) + '%)');
  }
};

// เรียกใช้งานทุกชั่วโมง
setInterval(monitorCredit, 60 * 60 * 1000);`}</code>
                    </pre>
                  </div>
                </div>

                {/* Scenario 5 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 5: บันทึกข้อมูลบัญชีเข้า Database</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// ดึงข้อมูลบัญชีและบันทึกใน Database
const syncAccountData = async () => {
  const response = await axios.get('https://slipaj.com/api/v3/account/info', {
    headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
  });

  const account = response.data.data.account;

  // บันทึกลง Database
  await db.accountStats.create({
    accountId: account.id,
    businessName: account.businessName,
    creditLimit: account.creditLimit,
    creditUsed: account.creditUsed,
    creditRemaining: account.creditRemaining,
    plan: account.plan,
    isActive: account.isActive,
    syncedAt: new Date(),
    processingTime: response.data.meta.processingTime
  });

  console.log('✓ บันทึกข้อมูลบัญชีสำเร็จ');
};

// Sync ทุก 24 ชั่วโมง
setInterval(syncAccountData, 24 * 60 * 60 * 1000);`
                        handleCopyToClipboard(code, "Scenario 5")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// ดึงข้อมูลบัญชีและบันทึกใน Database
const syncAccountData = async () => {
  const response = await axios.get('https://slipaj.com/api/v3/account/info', {
    headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
  });

  const account = response.data.data.account;

  // บันทึกลง Database
  await db.accountStats.create({
    accountId: account.id,
    businessName: account.businessName,
    creditLimit: account.creditLimit,
    creditUsed: account.creditUsed,
    creditRemaining: account.creditRemaining,
    plan: account.plan,
    isActive: account.isActive,
    syncedAt: new Date(),
    processingTime: response.data.meta.processingTime
  });

  console.log('✓ บันทึกข้อมูลบัญชีสำเร็จ');
};

// Sync ทุก 24 ชั่วโมง
setInterval(syncAccountData, 24 * 60 * 60 * 1000);`}</code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </TabGroupContent>
        </TabGroup>
      </div>
    </div>
  )
}

// ============ ACCOUNT USAGE ENDPOINT COMPONENT ============
function AccountUsageEndpointContent() {
  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${type} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="p-6 max-w-full">
        <EndpointHeader
          title="ดึงสถิติการใช้งาน"
          method="GET"
          endpoint="/api/v3/account/usage"
        />

        <TabGroup defaultValue="request" size="lg" color="brand-blue" fullWidth>
          <TabGroupList className="mb-6">
            <TabGroupTrigger value="request">รายละเอียด Request</TabGroupTrigger>
            <TabGroupTrigger value="response">รายละเอียด Response</TabGroupTrigger>
            <TabGroupTrigger value="usage">ตัวอย่างการใช้งาน</TabGroupTrigger>
          </TabGroupList>

          <TabGroupContent value="request">
            <div>
              {/* Requirements */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Requirements</h4>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ต้องมี API Secret ใน Header (Authorization: Bearer YOUR_API_KEY)</li>
                  <li>สามารถระบุ query parameter <code className="px-2 py-1 bg-gray-100 rounded text-sm">period</code> เพื่อเลือกช่วงเวลา (เช่น 7d, 30d, 90d)</li>
                  <li>ค่า default ของ period คือ <code className="px-2 py-1 bg-gray-100 rounded text-sm">30d</code></li>
                  <li>Credits Required: <span className="font-semibold text-green-600">0 credits (ฟรี)</span></li>
                </ul>
              </div>

              {/* Query Parameters Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Query Parameters</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Parameter</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Required</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">period</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Optional</td>
                        <td className="px-4 py-3 text-sm text-gray-700">ช่วงเวลาที่ต้องการดูสถิติ (7d, 30d, 90d)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"30d"</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Example */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Request</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `GET /api/v3/account/usage?period=30d
Headers:
  Authorization: Bearer YOUR_API_KEY`
                      handleCopyToClipboard(code, "Request")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`GET /api/v3/account/usage?period=30d
Headers:
  Authorization: Bearer YOUR_API_KEY`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="response">
            <div>
              {/* Response Fields Table */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">รายละเอียด Response</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Field</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Example</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">code</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">รหัสสถานะ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"200000"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">message</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อความอธิบายสถานะ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"Operation successful"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลสถิติการใช้งาน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.usage</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูลการใช้งาน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.usage.period</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ช่วงเวลาที่แสดงสถิติ</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"30d"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.usage.summary</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">สรุปการใช้งานโดยรวม</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.usage.summary.totalRequests</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวน Request ทั้งหมด</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">1500</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.usage.summary.totalCreditsUsed</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">จำนวน Credit ที่ใช้ไป</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">1500</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.usage.summary.successRate</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">อัตราความสำเร็จ (%)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">98.5</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.usage.summary.averageResponseTime</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เวลาตอบสนองเฉลี่ย (ms)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">250</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.usage.daily</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Array</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">สถิติแยกตามวัน</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">[...]</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.usage.byMethod</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">สถิติแยกตามประเภท API</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">data.timestamp</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">String (ISO 8601)</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เวลาที่ดึงข้อมูล</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">"2025-12-30T10:00:00.000Z"</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Object</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">ข้อมูล Metadata ของ Response</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">{'{ ... }'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">meta.processingTime</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">Number</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">เวลาในการประมวลผล Request (มิลลิวินาที)</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono whitespace-nowrap">15</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example Response */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">ตัวอย่าง Response</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `{
  "code": "200000",
  "message": "Operation successful",
  "data": {
    "usage": {
      "period": "30d",
      "summary": {
        "totalRequests": 1500,
        "totalCreditsUsed": 1500,
        "successRate": 98.5,
        "averageResponseTime": 250
      },
      "daily": [
        {
          "date": "2025-12-01",
          "requests": 50,
          "credits": 50,
          "successRate": "98.00"
        },
        {
          "date": "2025-12-02",
          "requests": 52,
          "credits": 52,
          "successRate": "100.00"
        }
      ],
      "byMethod": {
        "qr-code": {
          "requests": 800,
          "percentage": "53.3"
        },
        "image": {
          "requests": 500,
          "percentage": "33.3"
        },
        "base64": {
          "requests": 150,
          "percentage": "10.0"
        },
        "url": {
          "requests": 50,
          "percentage": "3.3"
        }
      },
      "topErrors": []
    },
    "timestamp": "2025-12-30T10:00:00.000Z"
  },
  "meta": {
    "processingTime": 15
  }
}`
                      handleCopyToClipboard(code, "Response")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`{
  "code": "200000",
  "message": "Operation successful",
  "data": {
    "usage": {
      "period": "30d",
      "summary": {
        "totalRequests": 1500,
        "totalCreditsUsed": 1500,
        "successRate": 98.5,
        "averageResponseTime": 250
      },
      "daily": [
        {
          "date": "2025-12-01",
          "requests": 50,
          "credits": 50,
          "successRate": "98.00"
        },
        {
          "date": "2025-12-02",
          "requests": 52,
          "credits": 52,
          "successRate": "100.00"
        }
      ],
      "byMethod": {
        "qr-code": {
          "requests": 800,
          "percentage": "53.3"
        },
        "image": {
          "requests": 500,
          "percentage": "33.3"
        },
        "base64": {
          "requests": 150,
          "percentage": "10.0"
        },
        "url": {
          "requests": 50,
          "percentage": "3.3"
        }
      },
      "topErrors": []
    },
    "timestamp": "2025-12-30T10:00:00.000Z"
  },
  "meta": {
    "processingTime": 15
  }
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </TabGroupContent>

          <TabGroupContent value="usage">
            <div>
              {/* cURL Example */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">1. ตัวอย่างการใช้งานด้วย cURL</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `curl -X GET "https://slipaj.com/api/v3/account/usage?period=30d" \\
  -H "Authorization: Bearer YOUR_API_KEY"`
                      handleCopyToClipboard(code, "cURL")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`curl -X GET "https://slipaj.com/api/v3/account/usage?period=30d" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</code>
                  </pre>
                </div>
              </div>

              {/* JavaScript/Axios Example */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">2. ตัวอย่างการใช้งานด้วย JavaScript (axios)</h4>
                <div className="relative bg-gray-800 rounded-lg">
                  <button
                    onClick={() => {
                      const code = `const axios = require('axios');

const getUsageStats = async (period = '30d') => {
  try {
    const response = await axios.get(\`https://slipaj.com/api/v3/account/usage?period=\${period}\`, {
      headers: {
        'Authorization': 'Bearer YOUR_API_KEY'
      }
    });

    if (response.data.code === '200000') {
      const usage = response.data.data.usage;
      console.log('สถิติการใช้งาน:', usage.period);
      console.log('Request ทั้งหมด:', usage.summary.totalRequests);
      console.log('อัตราสำเร็จ:', usage.summary.successRate + '%');
      console.log('เวลาตอบสนองเฉลี่ย:', usage.summary.averageResponseTime + 'ms');
      console.log('เวลาประมวลผล:', response.data.meta.processingTime, 'ms');
    }
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
  }
};

getUsageStats('30d');`
                      handleCopyToClipboard(code, "JavaScript")
                    }}
                    className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                    <code>{`const axios = require('axios');

const getUsageStats = async (period = '30d') => {
  try {
    const response = await axios.get(\`https://slipaj.com/api/v3/account/usage?period=\${period}\`, {
      headers: {
        'Authorization': 'Bearer YOUR_API_KEY'
      }
    });

    if (response.data.code === '200000') {
      const usage = response.data.data.usage;
      console.log('สถิติการใช้งาน:', usage.period);
      console.log('Request ทั้งหมด:', usage.summary.totalRequests);
      console.log('อัตราสำเร็จ:', usage.summary.successRate + '%');
      console.log('เวลาตอบสนองเฉลี่ย:', usage.summary.averageResponseTime + 'ms');
      console.log('เวลาประมวลผล:', response.data.meta.processingTime, 'ms');
    }
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
  }
};

getUsageStats('30d');`}</code>
                  </pre>
                </div>
              </div>

              {/* Scenario Examples */}
              <div className="mb-6 w-full">
                <h4 className="text-lg font-bold text-gray-900 mb-3">3. ตัวอย่างการใช้งานในสถานการณ์ต่างๆ</h4>

                {/* Scenario 1 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 1: ดูสถิติ 7 วันล่าสุด</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// ดูสถิติ 7 วันล่าสุด
const response = await axios.get('https://slipaj.com/api/v3/account/usage?period=7d', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const usage = response.data.data.usage;
console.log('สถิติ 7 วันล่าสุด');
console.log('Request ทั้งหมด:', usage.summary.totalRequests);
console.log('Credit ที่ใช้:', usage.summary.totalCreditsUsed);
console.log('อัตราสำเร็จ:', usage.summary.successRate + '%');`
                        handleCopyToClipboard(code, "Scenario 1")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// ดูสถิติ 7 วันล่าสุด
const response = await axios.get('https://slipaj.com/api/v3/account/usage?period=7d', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const usage = response.data.data.usage;
console.log('สถิติ 7 วันล่าสุด');
console.log('Request ทั้งหมด:', usage.summary.totalRequests);
console.log('Credit ที่ใช้:', usage.summary.totalCreditsUsed);
console.log('อัตราสำเร็จ:', usage.summary.successRate + '%');`}</code>
                    </pre>
                  </div>
                </div>

                {/* Scenario 2 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 2: วิเคราะห์การใช้งานแยกตาม API</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// วิเคราะห์การใช้งานแยกตาม API
const response = await axios.get('https://slipaj.com/api/v3/account/usage?period=30d', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const byMethod = response.data.data.usage.byMethod;

console.log('สถิติแยกตาม API:');
Object.entries(byMethod).forEach(([method, stats]) => {
  console.log(\`\${method}: \${stats.requests} requests (\${stats.percentage}%)\`);
});

// หา API ที่ใช้มากที่สุด
const mostUsed = Object.entries(byMethod)
  .sort((a, b) => b[1].requests - a[1].requests)[0];
console.log(\`\\nAPI ที่ใช้มากที่สุด: \${mostUsed[0]} (\${mostUsed[1].requests} requests)\`);`
                        handleCopyToClipboard(code, "Scenario 2")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// วิเคราะห์การใช้งานแยกตาม API
const response = await axios.get('https://slipaj.com/api/v3/account/usage?period=30d', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const byMethod = response.data.data.usage.byMethod;

console.log('สถิติแยกตาม API:');
Object.entries(byMethod).forEach(([method, stats]) => {
  console.log(\`\${method}: \${stats.requests} requests (\${stats.percentage}%)\`);
});

// หา API ที่ใช้มากที่สุด
const mostUsed = Object.entries(byMethod)
  .sort((a, b) => b[1].requests - a[1].requests)[0];
console.log(\`\\nAPI ที่ใช้มากที่สุด: \${mostUsed[0]} (\${mostUsed[1].requests} requests)\`);`}</code>
                    </pre>
                  </div>
                </div>

                {/* Scenario 3 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 3: สร้าง Chart แสดงการใช้งานรายวัน</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// ดึงข้อมูลเพื่อสร้าง Chart
const response = await axios.get('https://slipaj.com/api/v3/account/usage?period=30d', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const daily = response.data.data.usage.daily;

// จัดรูปแบบข้อมูลสำหรับ Chart.js
const chartData = {
  labels: daily.map(d => d.date),
  datasets: [{
    label: 'Daily Requests',
    data: daily.map(d => d.requests),
    borderColor: 'rgb(75, 192, 192)',
    tension: 0.1
  }]
};

console.log('Chart Data:', chartData);`
                        handleCopyToClipboard(code, "Scenario 3")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// ดึงข้อมูลเพื่อสร้าง Chart
const response = await axios.get('https://slipaj.com/api/v3/account/usage?period=30d', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const daily = response.data.data.usage.daily;

// จัดรูปแบบข้อมูลสำหรับ Chart.js
const chartData = {
  labels: daily.map(d => d.date),
  datasets: [{
    label: 'Daily Requests',
    data: daily.map(d => d.requests),
    borderColor: 'rgb(75, 192, 192)',
    tension: 0.1
  }]
};

console.log('Chart Data:', chartData);`}</code>
                    </pre>
                  </div>
                </div>

                {/* Scenario 4 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 4: ส่ง Email Report รายเดือน</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// สร้าง Monthly Report
const response = await axios.get('https://slipaj.com/api/v3/account/usage?period=30d', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const usage = response.data.data.usage;

const report = \`
📊 รายงานการใช้งาน API ประจำเดือน

สรุปภาพรวม:
- Request ทั้งหมด: \${usage.summary.totalRequests.toLocaleString()}
- Credit ที่ใช้: \${usage.summary.totalCreditsUsed.toLocaleString()}
- อัตราความสำเร็จ: \${usage.summary.successRate}%
- เวลาตอบสนองเฉลี่ย: \${usage.summary.averageResponseTime}ms

การใช้งานแยกตาม API:
\${Object.entries(usage.byMethod)
  .map(([method, stats]) => \`- \${method}: \${stats.requests} requests (\${stats.percentage}%)\`)
  .join('\\n')}
\`;

// ส่ง Email
await sendEmail({
  to: 'admin@example.com',
  subject: 'Monthly API Usage Report',
  body: report
});

console.log('✓ ส่ง Report สำเร็จ');`
                        handleCopyToClipboard(code, "Scenario 4")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// สร้าง Monthly Report
const response = await axios.get('https://slipaj.com/api/v3/account/usage?period=30d', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});

const usage = response.data.data.usage;

const report = \`
📊 รายงานการใช้งาน API ประจำเดือน

สรุปภาพรวม:
- Request ทั้งหมด: \${usage.summary.totalRequests.toLocaleString()}
- Credit ที่ใช้: \${usage.summary.totalCreditsUsed.toLocaleString()}
- อัตราความสำเร็จ: \${usage.summary.successRate}%
- เวลาตอบสนองเฉลี่ย: \${usage.summary.averageResponseTime}ms

การใช้งานแยกตาม API:
\${Object.entries(usage.byMethod)
  .map(([method, stats]) => \`- \${method}: \${stats.requests} requests (\${stats.percentage}%)\`)
  .join('\\n')}
\`;

// ส่ง Email
await sendEmail({
  to: 'admin@example.com',
  subject: 'Monthly API Usage Report',
  body: report
});

console.log('✓ ส่ง Report สำเร็จ');`}</code>
                    </pre>
                  </div>
                </div>

                {/* Scenario 5 */}
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-800 mb-2">Scenario 5: เปรียบเทียบสถิติหลายช่วงเวลา</h5>
                  <div className="relative bg-gray-800 rounded-lg">
                    <button
                      onClick={() => {
                        const code = `// เปรียบเทียบสถิติ 7 วัน vs 30 วัน
const [week, month] = await Promise.all([
  axios.get('https://slipaj.com/api/v3/account/usage?period=7d', {
    headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
  }),
  axios.get('https://slipaj.com/api/v3/account/usage?period=30d', {
    headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
  })
]);

const weekUsage = week.data.data.usage.summary;
const monthUsage = month.data.data.usage.summary;

console.log('เปรียบเทียบสถิติ:');
console.log('\\n7 วัน:');
console.log('- Requests:', weekUsage.totalRequests);
console.log('- Success Rate:', weekUsage.successRate + '%');
console.log('- Avg Response Time:', weekUsage.averageResponseTime + 'ms');

console.log('\\n30 วัน:');
console.log('- Requests:', monthUsage.totalRequests);
console.log('- Success Rate:', monthUsage.successRate + '%');
console.log('- Avg Response Time:', monthUsage.averageResponseTime + 'ms');

// คำนวณ Growth Rate
const growth = ((weekUsage.totalRequests * 30 / 7) / monthUsage.totalRequests - 1) * 100;
console.log(\`\\nGrowth Rate: \${growth.toFixed(2)}%\`);`
                        handleCopyToClipboard(code, "Scenario 5")
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <pre className="p-6 pr-12 text-sm text-gray-300 overflow-x-auto leading-relaxed">
                      <code>{`// เปรียบเทียบสถิติ 7 วัน vs 30 วัน
const [week, month] = await Promise.all([
  axios.get('https://slipaj.com/api/v3/account/usage?period=7d', {
    headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
  }),
  axios.get('https://slipaj.com/api/v3/account/usage?period=30d', {
    headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
  })
]);

const weekUsage = week.data.data.usage.summary;
const monthUsage = month.data.data.usage.summary;

console.log('เปรียบเทียบสถิติ:');
console.log('\\n7 วัน:');
console.log('- Requests:', weekUsage.totalRequests);
console.log('- Success Rate:', weekUsage.successRate + '%');
console.log('- Avg Response Time:', weekUsage.averageResponseTime + 'ms');

console.log('\\n30 วัน:');
console.log('- Requests:', monthUsage.totalRequests);
console.log('- Success Rate:', monthUsage.successRate + '%');
console.log('- Avg Response Time:', monthUsage.averageResponseTime + 'ms');

// คำนวณ Growth Rate
const growth = ((weekUsage.totalRequests * 30 / 7) / monthUsage.totalRequests - 1) * 100;
console.log(\`\\nGrowth Rate: \${growth.toFixed(2)}%\`);`}</code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </TabGroupContent>
        </TabGroup>
      </div>
    </div>
  )
}

// ============ MAIN PAGE COMPONENT ============
export default function ApiEndpointPage() {
  const [selectedEndpoint, setSelectedEndpoint] = useState<number | null>(1)

  const endpoints = [
    { id: 1, name: "ตรวจสอบสลิปด้วย QR-Code" },
    { id: 2, name: "ตรวจสอบสลิปด้วยรูปภาพ" },
    { id: 3, name: "ตรวจสอบสลิปด้วย Base64" },
    { id: 4, name: "ตรวจสอบสลิปด้วย URL" },
    { id: 5, name: "สร้าง PromptPay QR Code" },
    { id: 6, name: "สร้าง Bill Payment QR Code" },
    { id: 7, name: "ดึงข้อมูลสลิปเก่า" },
    { id: 8, name: "ดึงข้อมูลบัญชี" },
    { id: 9, name: "ดึงสถิติการใช้งาน" },
  ]

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 lg:mb-6">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">API Endpoint</h1>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 p-6">API Endpoint v3.0.0</h2>
          <hr className="border-gray-200" />

          <div className="grid grid-cols-1 lg:grid-cols-[350px_auto_1fr] min-h-[600px]">
            {/* Left Section - Endpoint List */}
            <div className="p-6">
              <div className="space-y-3">
                {endpoints.map((endpoint) => (
                  <Button
                    key={endpoint.id}
                    variant="ghost"
                    onClick={() => setSelectedEndpoint(endpoint.id)}
                    className={`w-full justify-start font-semibold rounded-lg px-6 py-3 h-auto text-left ${
                      selectedEndpoint === endpoint.id
                        ? "bg-brand-primary text-white hover:bg-brand-primary hover:text-white"
                        : "bg-gray-50 hover:bg-gray-100 text-brand-primary hover:text-brand-primary"
                    }`}
                  >
                    {endpoint.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="hidden lg:block border-l border-gray-200"></div>

            {/* Right Section */}
            <div className="hidden lg:block overflow-hidden">
              {selectedEndpoint === null ? (
                <div className="p-6 flex flex-col items-center justify-center h-full">
                  <Image
                    src="/icons/icon-connects.svg"
                    alt="API Endpoint icon"
                    width={192}
                    height={192}
                    className="w-48 h-48 text-gray-300 mb-4 opacity-20"
                  />
                  <p className="text-xl text-gray-300 font-medium text-center">
                    เลือก API Endpoint เพื่อดำเนินการต่อ
                  </p>
                </div>
              ) : selectedEndpoint === 1 ? (
                <QRCodeEndpointContent />
              ) : selectedEndpoint === 2 ? (
                <ImageEndpointContent />
              ) : selectedEndpoint === 3 ? (
                <Base64EndpointContent />
              ) : selectedEndpoint === 4 ? (
                <URLEndpointContent />
              ) : selectedEndpoint === 5 ? (
                <PromptPayEndpointContent />
              ) : selectedEndpoint === 6 ? (
                <BillPaymentEndpointContent />
              ) : selectedEndpoint === 7 ? (
                <HistoryEndpointContent />
              ) : selectedEndpoint === 8 ? (
                <AccountEndpointContent />
              ) : selectedEndpoint === 9 ? (
                <AccountUsageEndpointContent />
              ) : (
                <div className="p-6 flex flex-col items-center justify-center h-full">
                  <p className="text-gray-400">Coming soon...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
